import React, { useState, useEffect } from 'react';
import { formatCredits } from '../../utils/numberFormat';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  TablePagination
} from '@mui/material';
import {
  Search,
  Download,
  Visibility,
  Edit,
  Delete,
  Add,
  Refresh,
  Settings,
  Category,
  Palette,
  PhotoCamera,
  VideoLibrary,
  Style,
  ExpandMore,
  Save,
  Restore,
  Warning,
  CheckCircle,
  Block,
  Upload,
  CloudUpload
} from '@mui/icons-material';

import * as icons from '@mui/icons-material';
import { masterDataConfigAPI, modelFaceAPI } from '../../services/api';
import { appVersionAPI } from '../../services/appVersionAPI';
import * as subscriptionAPI from '../../services/subscriptionAPI';
import { usePermissions } from '../../contexts/PermissionContext';
import IconPicker from './IconPicker';
import SecureImage from '../Common/SecureImage';
import { API_URLS } from '../../config/apiConfig';

/**
 * MasterDataConfiguration Component
 * 
 * Features:
 * - CRUD operations for all master data types
 * - Pagination for all tables
 * - Sorting functionality for all columns (ascending/descending)
 * - Industry type filtering for categories and materials
 * - Icon picker for model types and output formats
 * - System settings management
 * - Credit settings configuration
 * 
 * Sorting:
 * - All tables support column sorting by clicking on column headers
 * - Sort state is maintained per tab
 * - Sorting triggers server-side API calls with sort parameters
 * - Sort direction toggles between ascending and descending
 */
const MasterDataConfiguration = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('MasterDataConfiguration', 'view');
  const canCreate = hasPermission('MasterDataConfiguration', 'create');
  const canUpdate = hasPermission('MasterDataConfiguration', 'update');
  const canDelete = hasPermission('MasterDataConfiguration', 'delete');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [modelTypes, setModelTypes] = useState([]);
  const [outputFormats, setOutputFormats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [backgroundTypes, setBackgroundTypes] = useState([]);
  const [mannequinColors, setMannequinColors] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [skinTones, setSkinTones] = useState([]); // New: Add state for skin tones
  const [ethnicities, setEthnicities] = useState([]); // New: Add state for ethnicities
  const [clothingTypes, setClothingTypes] = useState([]); // New: Add state for clothing types
  const [themes, setThemes] = useState([]); // New: Add state for themes
  const [bustFinishes, setBustFinishes] = useState([]); // New: Add state for bust finishes
  const [bustColors, setBustColors] = useState([]); // New: Add state for bust colors (dropdown)
  const [mediaQualities, setMediaQualities] = useState([]); // New: Add state for media qualities
  const [generationStatuses, setGenerationStatuses] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [categoryMappings, setCategoryMappings] = useState([]);
  const [categoryModelMappings, setCategoryModelMappings] = useState([]); // Add state for category model mappings
  const [bustTypes, setBustTypes] = useState([]); // New: Add state for bust types
  const [appVersions, setAppVersions] = useState([]); // New: Add state for app versions
  const [categoryGenderMappings, setCategoryGenderMappings] = useState([]); // New: Category gender mappings
  const [addonServices, setAddonServices] = useState([]); // New: Addon services state
  const [systemSettings, setSystemSettings] = useState({});

  // Subscription v2 master data
  const [v2Currencies, setV2Currencies] = useState([]);
  const [v2TaxRules, setV2TaxRules] = useState([]);
  const [v2Dialog, setV2Dialog] = useState({ open: false, type: null, item: null });
  const [v2Form, setV2Form] = useState({});
  const [v2Submitting, setV2Submitting] = useState(false);
  const [v2TaxComponents, setV2TaxComponents] = useState([]);
  const [v2DeleteConfirm, setV2DeleteConfirm] = useState({ open: false, type: null, id: null, label: '' });
  const [v2Errors, setV2Errors] = useState({});
  const [v2Touched, setV2Touched] = useState({});
  const canManageCurrencies = hasPermission('MasterDataConfiguration', 'manage_currencies');
  const canManageTaxRules   = hasPermission('MasterDataConfiguration', 'manage_tax_rules');

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [categoryIndustryFilter, setCategoryIndustryFilter] = useState('all');
  const [materialIndustryFilter, setMaterialIndustryFilter] = useState('all');

  // Pagination states for each tab
  const [restrictedDomains, setRestrictedDomains] = useState([]);
  const [paginationState, setPaginationState] = useState({
    modelTypes: { page: 0, rowsPerPage: 10 },
    outputFormats: { page: 0, rowsPerPage: 10 },
    categories: { page: 0, rowsPerPage: 10 },
    materials: { page: 0, rowsPerPage: 10 },
    backgroundTypes: { page: 0, rowsPerPage: 10 },
    mannequinColors: { page: 0, rowsPerPage: 10 },
    genderOptions: { page: 0, rowsPerPage: 10 },
    skinTones: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for skin tones
    ethnicities: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for ethnicities
    clothingTypes: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for clothing types
    themes: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for themes
    bustFinishes: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for bust finishes
    mediaQualities: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for media qualities
    generationStatuses: { page: 0, rowsPerPage: 10 },
    industryTypes: { page: 0, rowsPerPage: 10 },
    categoryMappings: { page: 0, rowsPerPage: 10 },
    categoryModelMappings: { page: 0, rowsPerPage: 10 }, // Add pagination state for category model mappings
    bustTypes: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for bust types
    appVersions: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for app versions
    categoryGenderMappings: { page: 0, rowsPerPage: 10 },
    restricted_email_domains: { page: 0, rowsPerPage: 10 },
    addonServices: { page: 0, rowsPerPage: 10 }, // New: Add pagination state for addon services
  });

  // Sorting states for each tab
  const [sortingState, setSortingState] = useState({
    modelTypes: { orderBy: 'name', order: 'asc' },
    outputFormats: { orderBy: 'name', order: 'asc' },
    categories: { orderBy: 'name', order: 'asc' },
    materials: { orderBy: 'name', order: 'asc' },
    backgroundTypes: { orderBy: 'name', order: 'asc' },
    mannequinColors: { orderBy: 'name', order: 'asc' },
    genderOptions: { orderBy: 'name', order: 'asc' },
    skinTones: { orderBy: 'name', order: 'asc' },
    ethnicities: { orderBy: 'name', order: 'asc' },
    clothingTypes: { orderBy: 'name', order: 'asc' },
    themes: { orderBy: 'name', order: 'asc' },
    bustFinishes: { orderBy: 'name', order: 'asc' },
    mediaQualities: { orderBy: 'name', order: 'asc' },
    generationStatuses: { orderBy: 'name', order: 'asc' },
    industryTypes: { orderBy: 'name', order: 'asc' },
    categoryMappings: { orderBy: 'id', order: 'asc' },
    categoryModelMappings: { orderBy: 'id', order: 'asc' }, // Add sorting state for category model mappings
    bustTypes: { orderBy: 'name', order: 'asc' }, // New: Add sorting state for bust types
    appVersions: { orderBy: 'created_at', order: 'desc' }, // New: Add sorting state for app versions
    categoryGenderMappings: { orderBy: 'id', order: 'asc' },
    restricted_email_domains: { orderBy: 'domain', order: 'asc' },
    addonServices: { orderBy: 'sort_order', order: 'asc' }, // New: Add sorting state for addon services
  });

  // Total counts for each tab (separate from pagination to avoid triggering effects)
  const [totalCounts, setTotalCounts] = useState({
    modelTypes: 0,
    outputFormats: 0,
    categories: 0,
    materials: 0,
    backgroundTypes: 0,
    mannequinColors: 0,
    genderOptions: 0,
    skinTones: 0, // New: Add total counts for skin tones
    ethnicities: 0, // New: Add total counts for ethnicities
    clothingTypes: 0, // New: Add total counts for clothing types
    themes: 0, // New: Add total counts for themes
    bustFinishes: 0, // New: Add total counts for bust finishes
    mediaQualities: 0, // New: Add total counts for media qualities
    generationStatuses: 0,
    industryTypes: 0,
    categoryMappings: 0,
    categoryModelMappings: 0, // Add total count for category model mappings
    bustTypes: 0, // New: Add total counts for bust types
    appVersions: 0, // New: Add total counts for app versions
    categoryGenderMappings: 0,
    restricted_email_domains: 0,
    addonServices: 0, // New: Add total counts for addon services
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    icon: '', // Icon field for model types
    is_active: true,
    sort_order: 0,
    metadata: {},
    // Image and color fields
    preview_image_url: '',
    hex_color: '',
    // Credit settings fields
    image_cost: 10,
    gif_cost: 20,
    video_cost: 30,
    watermark_cost: 5,
    brand_logo_cost: 5,
    has_watermark: false,
    has_brand_logo: false,
    // Category mappings fields
    material_id: '',
    ethnicity_id: '',
    clothing_type_id: '',
    bust_finish_id: '',
    bust_color_id: '',
    // Category-model mapping fields
    category_id: '',
    model_type_id: '',
    // Category-gender mapping fields
    gender_option_id: '',
    // App version fields
    android_version_code: '',
    android_release_note: '',
    android_version_name: '',
    android_update_redirection_url: '',
    android_deep_link_schema: '',
    ios_version_code: '',
    ios_release_note: '',
    ios_version_name: '',
    ios_update_redirection_url: '',
    ios_deep_link_schema: '',
    universal_link_domain: '',
    android_app_link_domain: '',
    enable_deep_linking: true,
    description: '',
    release_date: '',
    min_required_version: '',
    // Addon service fields
    code: '',
    icon_name: ''
  });

  // Icon picker dialog state
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  // Reset confirmation dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Validation error states for credit costs
  const [validationErrors, setValidationErrors] = useState({
    image_cost: '',
    gif_cost: '',
    video_cost: ''
  });

  // Form validation errors state
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        // Always load industry types first since they're needed for filters
        await loadIndustryTypes();
        // Load credit settings
        await loadCreditSettings();
        // Load all bust colors and finishes for dropdowns (needed for category mappings)
        await loadAllBustColors();
        await loadAllBustFinishes();
        // Load system settings for the System Settings tab
        await loadSystemSettings();
        // Then load the rest of the data
        await loadAllData();
      } catch (error) {
        console.error('Error initializing data:', error);
        setError('Failed to initialize data');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Reset industry filters when tab changes
  useEffect(() => {
    setCategoryIndustryFilter('all');
    setMaterialIndustryFilter('all');
  }, [activeTab]);

  // Clear conditional fields when model type changes in category mappings
  useEffect(() => {
    if (dialogType === 'category_mappings' && formData.model_type_id) {
      const selectedModelType = modelTypes.find(mt => mt.id === formData.model_type_id);
      const isHumanModel = selectedModelType && (
        selectedModelType.name.toLowerCase().includes('human') ||
        selectedModelType.display_name.toLowerCase().includes('human')
      );
      const isBustModel = selectedModelType && (
        selectedModelType.name.toLowerCase().includes('bust') ||
        selectedModelType.display_name.toLowerCase().includes('bust')
      );

      // Clear fields that don't apply to the selected model type
      if (isHumanModel) {
        // Clear Bust-specific fields
        setFormData(prev => ({
          ...prev,
          bust_finish_id: '',
          bust_color_id: ''
        }));
      } else if (isBustModel) {
        // Clear Human-specific fields
        setFormData(prev => ({
          ...prev,
          ethnicity_id: '',
          clothing_type_id: ''
        }));
      } else {
        // Clear all conditional fields for other model types
        setFormData(prev => ({
          ...prev,
          ethnicity_id: '',
          clothing_type_id: '',
          bust_finish_id: '',
          bust_color_id: ''
        }));
      }
    }
  }, [formData.model_type_id, dialogType, modelTypes]);

  // Helper function to clean form data for category mappings
  const cleanCategoryMappingData = (data) => {
    return {
      ...data,
      ethnicity_id: data.ethnicity_id === '' ? null : data.ethnicity_id,
      clothing_type_id: data.clothing_type_id === '' ? null : data.clothing_type_id,
      bust_finish_id: data.bust_finish_id === '' ? null : data.bust_finish_id,
      bust_color_id: data.bust_color_id === '' ? null : data.bust_color_id
    };
  };

  // Helper function to validate category mapping data
  const validateCategoryMappingData = (data) => {
    const errors = [];

    // Check required fields
    if (!data.model_type_id) errors.push('Model Type is required');
    if (!data.category_id) errors.push('Category is required');

    // Check conditional field requirements based on model type
    const selectedModelType = modelTypes.find(mt => mt.id === data.model_type_id);
    if (selectedModelType) {
      const isHumanModel = selectedModelType.name.toLowerCase().includes('human') ||
        selectedModelType.display_name.toLowerCase().includes('human');
      const isBustModel = selectedModelType.name.toLowerCase().includes('bust') ||
        selectedModelType.display_name.toLowerCase().includes('bust');

      if (isHumanModel) {
        if (!data.ethnicity_id) errors.push('Ethnicity is required for Human models');
        // Note: Clothing Type is now OPTIONAL - can be left null to hide in frontend
      }

      if (isBustModel) {
        if (!data.bust_finish_id) errors.push('Bust Finish is required for Bust models');
        if (!data.bust_color_id) errors.push('Bust Color is required for Bust models');
      }
    }

    return errors;
  };

  // Form validation functions
  const validateForm = (data, type) => {
    const errors = {};

    // Common validations for all types (except those without name/display_name fields)
    if (type !== 'app_versions' && type !== 'category_model_mappings' && type !== 'category_mappings' && type !== 'category_gender_mappings' && type !== 'restricted_email_domains') {
      if (!data.name || !data.name.trim()) {
        errors.name = 'Name is required';
      } else if (data.name.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters long';
      } else if (data.name.trim().length > 100) {
        errors.name = 'Name must be less than 100 characters';
      }

      if (!data.display_name || !data.display_name.trim()) {
        errors.display_name = 'Display Name is required';
      } else if (data.display_name.trim().length < 2) {
        errors.display_name = 'Display Name must be at least 2 characters long';
      } else if (data.display_name.trim().length > 100) {
        errors.display_name = 'Display Name must be less than 100 characters';
      }
    }

    if (data.description && data.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }

    // Type-specific validations
    switch (type) {
      case 'categories':
      case 'materials':
        if (!data.industry_type_id) {
          errors.industry_type_id = 'Industry Type is required';
        }
        break;

      case 'output_formats':
        if (data.base_credits !== undefined && (data.base_credits < 0 || data.base_credits > 10000)) {
          errors.base_credits = 'Base credits must be between 0 and 10000';
        }
        break;

      case 'bust_colors':
      case 'skin_tones':
        if (data.hex_color && !/^#[0-9A-Fa-f]{6}$/.test(data.hex_color)) {
          errors.hex_color = 'Hex color must be a valid color code (e.g., #FF0000)';
        }
        break;

      case 'generation_statuses':
        if (data.color_code && !/^#[0-9A-Fa-f]{6}$/.test(data.color_code)) {
          errors.color_code = 'Color code must be a valid color code (e.g., #FF0000)';
        }
        break;

      case 'category_mappings':
        if (!data.category_id) {
          errors.category_id = 'Category is required';
        }
        if (!data.model_type_id) {
          errors.model_type_id = 'Model Type is required';
        }
        // Additional validation for conditional fields will be handled in the specific form
        break;

      case 'category_model_mappings':
        if (!data.category_id) {
          errors.category_id = 'Category is required';
        }
        if (!data.model_type_id) {
          errors.model_type_id = 'Model Type is required';
        }
        break;

      case 'category_gender_mappings':
        if (!data.category_id) {
          errors.category_id = 'Category is required';
        }
        if (!data.gender_option_id) {
          errors.gender_option_id = 'Gender Option is required';
        }
        break;

      case 'app_versions':
        if (!data.android_version_code || !data.android_version_code.trim()) {
          errors.android_version_code = 'Android Version Code is required';
        }
        if (!data.android_version_name || !data.android_version_name.trim()) {
          errors.android_version_name = 'Android Version Name is required';
        }
        if (!data.ios_version_code || !data.ios_version_code.trim()) {
          errors.ios_version_code = 'iOS Version Code is required';
        }
        if (!data.ios_version_name || !data.ios_version_name.trim()) {
          errors.ios_version_name = 'iOS Version Name is required';
        }
        break;

      case 'restricted_email_domains':
        if (!data.domain || !data.domain.trim()) {
          errors.domain = 'Domain is required';
        } else if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(data.domain.trim())) {
          errors.domain = 'Invalid domain format (e.g., example.com)';
        }
        break;

      case 'media_qualities':
        if (!data.applies_to || !['IMAGE', 'VIDEO'].includes(data.applies_to)) {
          errors.applies_to = 'Applies To must be IMAGE or VIDEO';
        }
        break;
    }

    return errors;
  };

  const validateField = (fieldName, value, type) => {
    const errors = {};

    // Common types that don't use name/display_name
    const isSpecialType = ['app_versions', 'category_model_mappings', 'category_mappings', 'category_gender_mappings', 'restricted_email_domains'].includes(type);

    switch (fieldName) {
      case 'name':
        if (isSpecialType) break;
        if (!value || !value.trim()) {
          errors.name = 'Name is required';
        } else if (value.trim().length < 2) {
          errors.name = 'Name must be at least 2 characters long';
        } else if (value.trim().length > 100) {
          errors.name = 'Name must be less than 100 characters';
        }
        break;
      case 'display_name':
        if (isSpecialType) break;
        if (!value || !value.trim()) {
          errors.display_name = 'Display Name is required';
        } else if (value.trim().length < 2) {
          errors.display_name = 'Display Name must be at least 2 characters long';
        } else if (value.trim().length > 100) {
          errors.display_name = 'Display Name must be less than 100 characters';
        }
        break;
      case 'description':
        if (value && value.length > 500) {
          errors.description = 'Description must be less than 500 characters';
        }
        break;
      case 'industry_type_id':
        if ((type === 'categories' || type === 'materials') && !value) {
          errors.industry_type_id = 'Industry Type is required';
        }
        break;
      case 'base_credits':
        if (value !== undefined && (value < 0 || value > 10000)) {
          errors.base_credits = 'Base credits must be between 0 and 10000';
        }
        break;
      case 'hex_color':
        if (value && !/^#[0-9A-Fa-f]{6}$/.test(value)) {
          errors.hex_color = 'Hex color must be a valid color code (e.g., #FF0000)';
        }
        break;
      case 'color_code':
        if (value && !/^#[0-9A-Fa-f]{6}$/.test(value)) {
          errors.color_code = 'Color code must be a valid color code (e.g., #FF0000)';
        }
        break;
      case 'model_type_id':
        if ((type === 'category_mappings' || type === 'category_model_mappings') && !value) {
          errors.model_type_id = 'Model Type is required';
        }
        break;
      case 'category_id':
        if ((type === 'category_mappings' || type === 'category_model_mappings') && !value) {
          errors.category_id = 'Category is required';
        }
        break;
      case 'android_version_code':
        if (type === 'app_versions' && (!value || !value.trim())) {
          errors.android_version_code = 'Android Version Code is required';
        }
        break;
      case 'android_version_name':
        if (type === 'app_versions' && (!value || !value.trim())) {
          errors.android_version_name = 'Android Version Name is required';
        }
        break;
      case 'ios_version_code':
        if (type === 'app_versions' && (!value || !value.trim())) {
          errors.ios_version_code = 'iOS Version Code is required';
        }
        break;
      case 'ios_version_name':
        if (type === 'app_versions' && (!value || !value.trim())) {
          errors.ios_version_name = 'iOS Version Name is required';
        }
        break;
      case 'domain':
        if (type === 'restricted_email_domains' && (!value || !value.trim())) {
          errors.domain = 'Domain is required';
        } else if (type === 'restricted_email_domains' && !/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim())) {
          errors.domain = 'Invalid domain format (e.g., example.com)';
        }
        break;
    }

    return errors;
  };

  const handleFieldChange = (fieldName, value) => {
    // Update form data
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Clear previous error for this field
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });

    // Validate the field
    const fieldErrors = validateField(fieldName, value, dialogType);
    if (Object.keys(fieldErrors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...fieldErrors }));
    }
  };

  const loadV2Currencies = async () => {
    try {
      const res = await subscriptionAPI.getCurrencies();
      setV2Currencies(res.data || []);
    } catch (e) {
      console.error('loadV2Currencies', e);
      setError(e.response?.data?.message || 'Failed to load currencies');
    }
  };

  const loadV2TaxRules = async () => {
    try {
      const res = await subscriptionAPI.getTaxRules();
      setV2TaxRules(res.data || []);
    } catch (e) {
      console.error('loadV2TaxRules', e);
      setError(e.response?.data?.message || 'Failed to load tax rules');
    }
  };

  const openV2Dialog = (type, item = null) => {
    if (type === 'currency') {
      setV2Form(item ? { display_name: item.display_name, symbol: item.symbol, decimal_places: item.decimal_places, is_default: item.is_default } : { code: '', display_name: '', symbol: '', decimal_places: 2, is_default: false });
    } else {
      setV2Form(item ? { name: item.name, currency_code: item.currency_code, supply_type: item.supply_type, effective_rate: item.effective_rate, default_hsn_sac: item.default_hsn_sac || '', is_default: item.is_default } : { name: '', currency_code: 'INR', supply_type: 'intra_state', effective_rate: '', default_hsn_sac: '', is_default: false });
      setV2TaxComponents(item ? (item.components || []).map(c => ({ component_type: c.component_type, rate_percent: c.rate_percent })) : []);
    }
    setV2Errors({});
    setV2Touched({});
    setV2Dialog({ open: true, type, item });
  };

  const closeV2Dialog = () => {
    setV2Dialog({ open: false, type: null, item: null });
    setV2Errors({});
    setV2Touched({});
  };

  const validateCurrencyField = (name, value) => {
    if (name === 'code') {
      if (!value || !value.trim()) return 'Currency code is required';
      if (!/^[A-Z]{3}$/.test(value.trim())) return 'Must be exactly 3 uppercase letters (e.g. INR)';
    }
    if (name === 'display_name') {
      if (!value || !value.trim()) return 'Display name is required';
      if (value.trim().length > 200) return 'Max 200 characters';
    }
    if (name === 'symbol') {
      if (!value || !value.trim()) return 'Symbol is required';
      if (value.trim().length > 10) return 'Max 10 characters';
    }
    if (name === 'decimal_places') {
      const n = parseInt(value);
      if (isNaN(n)) return 'Required';
      if (n < 0 || n > 4) return 'Must be between 0 and 4';
    }
    return '';
  };

  const validateTaxRuleField = (name, value) => {
    if (name === 'name') {
      if (!value || !value.trim()) return 'Name is required';
      if (value.trim().length > 200) return 'Max 200 characters';
    }
    if (name === 'effective_rate') {
      if (value === '' || value === null || value === undefined) return 'Effective rate is required';
      const n = parseFloat(value);
      if (isNaN(n)) return 'Must be a number';
      if (n < 0 || n > 100) return 'Must be between 0 and 100';
    }
    return '';
  };

  const saveV2Currency = async () => {
    const fields = v2Dialog.item
      ? ['display_name', 'symbol', 'decimal_places']
      : ['code', 'display_name', 'symbol', 'decimal_places'];
    const errors = {};
    const touched = {};
    fields.forEach(f => {
      touched[f] = true;
      const err = validateCurrencyField(f, v2Form[f]);
      if (err) errors[f] = err;
    });
    setV2Touched(t => ({ ...t, ...touched }));
    if (Object.keys(errors).length > 0) { setV2Errors(errors); return; }
    setV2Submitting(true);
    try {
      if (v2Dialog.item) {
        await subscriptionAPI.updateCurrency(v2Dialog.item.id, v2Form);
      } else {
        await subscriptionAPI.createCurrency(v2Form);
      }
      setSuccess(v2Dialog.item ? 'Currency updated' : 'Currency created');
      closeV2Dialog();
      loadV2Currencies();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save currency');
    } finally {
      setV2Submitting(false);
    }
  };

  const deleteV2Currency = (id, label) =>
    setV2DeleteConfirm({ open: true, type: 'currency', id, label });

  const confirmV2Delete = async () => {
    const { type, id } = v2DeleteConfirm;
    setV2DeleteConfirm({ open: false, type: null, id: null, label: '' });
    try {
      if (type === 'currency') {
        await subscriptionAPI.deleteCurrency(id);
        setSuccess('Currency deactivated');
        loadV2Currencies();
      } else {
        await subscriptionAPI.deleteTaxRule(id);
        setSuccess('Tax rule deactivated');
        loadV2TaxRules();
      }
    } catch (e) { setError(e.response?.data?.message || 'Failed to deactivate'); }
  };

  const saveV2TaxRule = async () => {
    const errors = {};
    const nameErr = validateTaxRuleField('name', v2Form.name);
    if (nameErr) errors.name = nameErr;
    const rateErr = validateTaxRuleField('effective_rate', v2Form.effective_rate);
    if (rateErr) errors.effective_rate = rateErr;
    if (v2TaxComponents.length > 0) {
      const hasEmpty = v2TaxComponents.some(c => c.rate_percent === '' || c.rate_percent === null || c.rate_percent === undefined);
      if (hasEmpty) {
        errors.components = 'All component rates are required';
      } else {
        const sum = v2TaxComponents.reduce((acc, c) => acc + (parseFloat(c.rate_percent) || 0), 0);
        const rate = parseFloat(v2Form.effective_rate) || 0;
        if (Math.abs(sum - rate) > 0.01) errors.components = `Component rates sum (${sum}%) must equal effective rate (${rate}%)`;
      }
    }
    setV2Touched(t => ({ ...t, name: true, effective_rate: true }));
    if (Object.keys(errors).length > 0) { setV2Errors(errors); return; }
    setV2Submitting(true);
    try {
      const payload = { ...v2Form, components: v2TaxComponents.map(c => ({ component_type: c.component_type, rate_percent: parseFloat(c.rate_percent) })) };
      if (v2Dialog.item) {
        await subscriptionAPI.updateTaxRule(v2Dialog.item.id, payload);
      } else {
        await subscriptionAPI.createTaxRule(payload);
      }
      setSuccess(v2Dialog.item ? 'Tax rule updated' : 'Tax rule created');
      closeV2Dialog();
      loadV2TaxRules();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save tax rule');
    } finally {
      setV2Submitting(false);
    }
  };

  const deleteV2TaxRule = (id, label) =>
    setV2DeleteConfirm({ open: true, type: 'tax_rule', id, label });

  // Load data when tab changes, pagination changes, or filters change
  useEffect(() => {
    // Debounce function to prevent multiple rapid API calls
    const debounceTimer = setTimeout(() => {
      if (activeTab === 0) loadIndustryTypes();
      else if (activeTab === 1) loadCategories();
      else if (activeTab === 2) loadMaterials();
      else if (activeTab === 3) loadModelTypes();
      else if (activeTab === 4) loadOutputFormats();
      else if (activeTab === 5) loadBackgroundTypes();
      else if (activeTab === 6) loadMannequinColors();
      else if (activeTab === 7) loadGenderOptions();
      else if (activeTab === 8) loadSkinTones(); // New: Load skin tones
      else if (activeTab === 9) loadEthnicities(); // New: Load ethnicities
      else if (activeTab === 10) loadClothingTypes(); // New: Load clothing types
      else if (activeTab === 11) loadAllBustFinishes(); // New: Load bust finishes
      else if (activeTab === 12) loadMediaQualities(); // New: Load media qualities
      else if (activeTab === 13) loadGenerationStatuses();
      else if (activeTab === 14) loadBustTypes(); // New: Load bust types
      else if (activeTab === 15) {
        loadCategoryMappings();
        // Also load categories, model types, materials, ethnicities, clothing types, core materials, and bust colors for the dropdown options
        loadAllCategories();
        loadAllModelTypes();
        loadMaterials();
        loadEthnicities();
        loadClothingTypes();
        loadAllBustFinishes();
        loadAllBustColors();
      }
      else if (activeTab === 16) {
        loadCategoryModelMappings();
        // Also load categories and model types for the dropdown options
        loadAllCategories();
        loadAllModelTypes();
      }
      else if (activeTab === 17) {
        loadAppVersions(); // Load app versions for the new tab
      }
      else if (activeTab === 19) {
        loadCategoryGenderMappings();
        loadAllCategories();
        loadGenderOptions();
      }
      else if (activeTab === 20) loadRestrictedDomains();
      else if (activeTab === 21) loadAddonServices(); // New: Load addon services
      else if (activeTab === 22) loadThemes(); // New: Load themes
      else if (activeTab === 23) loadV2Currencies();
      else if (activeTab === 24) { loadV2TaxRules(); loadV2Currencies(); }
    }, 100); // Small debounce to catch rapid state updates

    return () => clearTimeout(debounceTimer);
  }, [
    activeTab,
    // Include pagination states for consistency (only the active tab's pagination will actually be used)
    paginationState.industryTypes.page,
    paginationState.industryTypes.rowsPerPage,
    paginationState.categories.page,
    paginationState.categories.rowsPerPage,
    paginationState.materials.page,
    paginationState.materials.rowsPerPage,
    paginationState.modelTypes.page,
    paginationState.modelTypes.rowsPerPage,
    paginationState.outputFormats.page,
    paginationState.outputFormats.rowsPerPage,
    paginationState.backgroundTypes.page,
    paginationState.backgroundTypes.rowsPerPage,
    paginationState.mannequinColors.page,
    paginationState.mannequinColors.rowsPerPage,
    paginationState.genderOptions.page,
    paginationState.genderOptions.rowsPerPage,
    paginationState.skinTones.page,
    paginationState.skinTones.rowsPerPage,
    paginationState.ethnicities.page,
    paginationState.ethnicities.rowsPerPage,
    paginationState.clothingTypes.page,
    paginationState.clothingTypes.rowsPerPage,
    paginationState.bustFinishes.page,
    paginationState.bustFinishes.rowsPerPage,
    paginationState.mediaQualities.page,
    paginationState.mediaQualities.rowsPerPage,
    paginationState.generationStatuses.page,
    paginationState.generationStatuses.rowsPerPage,
    paginationState.categoryMappings.page,
    paginationState.categoryMappings.rowsPerPage,
    paginationState.bustTypes.page,
    paginationState.bustTypes.rowsPerPage,
    paginationState.appVersions.page,
    paginationState.appVersions.rowsPerPage,
    paginationState.restricted_email_domains.page,
    paginationState.restricted_email_domains.rowsPerPage,
    // Add filter states to dependencies to trigger reloads when filters change
    categoryIndustryFilter,
    materialIndustryFilter,
    // Add sorting states to dependencies to trigger reloads when sorting changes
    sortingState.industryTypes,
    sortingState.categories,
    sortingState.materials,
    sortingState.modelTypes,
    sortingState.outputFormats,
    sortingState.backgroundTypes,
    sortingState.mannequinColors,
    sortingState.genderOptions,
    sortingState.skinTones,
    sortingState.ethnicities,
    sortingState.clothingTypes,
    sortingState.bustFinishes,
    sortingState.mediaQualities,
    sortingState.generationStatuses,
    sortingState.categoryMappings,
    paginationState.categoryModelMappings.page,
    paginationState.categoryModelMappings.rowsPerPage,
    sortingState.categoryModelMappings,
    sortingState.bustTypes,
    sortingState.appVersions,
    sortingState.restricted_email_domains,
    paginationState.addonServices.page,
    paginationState.addonServices.rowsPerPage,
    sortingState.addonServices,
    paginationState.themes.page,
    paginationState.themes.rowsPerPage,
    sortingState.themes
  ]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load all data for the active tab
      if (activeTab === 0) await loadIndustryTypes();
      else if (activeTab === 1) await loadCategories();
      else if (activeTab === 2) await loadMaterials();
      else if (activeTab === 3) await loadModelTypes();
      else if (activeTab === 4) await loadOutputFormats();
      else if (activeTab === 5) await loadBackgroundTypes();
      else if (activeTab === 6) await loadMannequinColors();
      else if (activeTab === 7) await loadGenderOptions();
      else if (activeTab === 8) await loadSkinTones(); // New: Load skin tones
      else if (activeTab === 9) await loadEthnicities(); // New: Load ethnicities
      else if (activeTab === 10) await loadClothingTypes(); // New: Load clothing types
      else if (activeTab === 11) await loadAllBustFinishes(); // New: Load bust finishes
      else if (activeTab === 12) await loadMediaQualities(); // New: Load media qualities
      else if (activeTab === 13) await loadGenerationStatuses();
      else if (activeTab === 14) await loadBustTypes(); // New: Load bust types
      else if (activeTab === 15) {
        await loadCategoryMappings();
        // Also load categories, model types, materials, ethnicities, clothing types, core materials, and bust colors for the dropdown options
        await loadAllCategories();
        await loadAllModelTypes();
        await loadMaterials();
        await loadEthnicities();
        await loadClothingTypes();
        await loadAllBustFinishes();
        await loadAllBustColors();
      }
      else if (activeTab === 16) {
        await loadCategoryModelMappings(); // Load category model mappings for the new tab
        // Also load categories and model types for the dropdown options
        await loadAllCategories();
        await loadAllModelTypes();
      }
      else if (activeTab === 17) {
        await loadAppVersions(); // Load app versions for the new tab
      }
      else if (activeTab === 19) {
        await loadCategoryGenderMappings();
        await loadAllCategories();
        await loadGenderOptions();
      }
      else if (activeTab === 20) await loadRestrictedDomains();
      else if (activeTab === 21) await loadAddonServices(); // New: Load addon services
      else if (activeTab === 22) await loadThemes(); // New: Load themes
      else if (activeTab === 23) await loadV2Currencies();
      else if (activeTab === 24) { await loadV2TaxRules(); await loadV2Currencies(); }
      // Note: activeTab === 18 is System Settings (no loading needed as it handles its own state)
      // Note: Industry types are now loaded on component mount
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  };

  const loadModelTypes = async () => {
    console.log('🔍 Loading Model Types...', {
      page: paginationState.modelTypes.page + 1,
      per_page: paginationState.modelTypes.rowsPerPage,
      sort_by: sortingState.modelTypes.orderBy,
      sort_order: sortingState.modelTypes.order
    });

    try {
      const params = {
        page: paginationState.modelTypes.page + 1,
        per_page: paginationState.modelTypes.rowsPerPage,
        sort_by: sortingState.modelTypes.orderBy,
        sort_order: sortingState.modelTypes.order
      };

      const response = await masterDataConfigAPI.getModelTypes(params);
      console.log('✅ Model Types API Response:', response.data);

      const modelTypesData = response.data.data || [];
      const totalCount = response.data.total || 0;

      console.log(`📊 Setting Model Types: ${modelTypesData.length} items, total: ${totalCount}`);

      setModelTypes(modelTypesData);
      setTotalCounts(prev => ({ ...prev, modelTypes: totalCount }));

    } catch (error) {
      console.error('❌ Failed to load model types:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const loadOutputFormats = async () => {
    try {
      const params = {
        page: paginationState.outputFormats.page + 1,
        per_page: paginationState.outputFormats.rowsPerPage,
        sort_by: sortingState.outputFormats.orderBy,
        sort_order: sortingState.outputFormats.order
      };
      const response = await masterDataConfigAPI.getOutputFormats(params);
      setOutputFormats(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, outputFormats: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load output formats:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const { page, rowsPerPage } = paginationState.categories;
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        include_inactive: false,  // Changed from true to false
        sort_by: sortingState.categories.orderBy,
        sort_order: sortingState.categories.order
      };

      // Only add industry_type_id to params if a filter is selected and not 'all'
      if (categoryIndustryFilter && categoryIndustryFilter !== 'all') {
        params.industry_type_id = categoryIndustryFilter;
      }

      const response = await masterDataConfigAPI.getCategories(params);
      setCategories(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, categories: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load categories:', error);
      setError('Failed to load categories');
    }
  };

  const loadMaterials = async () => {
    try {
      const { page, rowsPerPage } = paginationState.materials;
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        include_inactive: false,  // Changed from true to false
        sort_by: sortingState.materials.orderBy,
        sort_order: sortingState.materials.order
      };

      // Only add industry_type_id to params if a filter is selected and not 'all'
      if (materialIndustryFilter && materialIndustryFilter !== 'all') {
        params.industry_type_id = materialIndustryFilter;
      }

      const response = await masterDataConfigAPI.getMaterials(params);
      setMaterials(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, materials: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load materials:', error);
      setError('Failed to load materials');
    }
  };

  const loadBackgroundTypes = async () => {
    try {
      const params = {
        page: paginationState.backgroundTypes.page + 1,
        per_page: paginationState.backgroundTypes.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.backgroundTypes.orderBy,
        sort_order: sortingState.backgroundTypes.order
      };
      const response = await masterDataConfigAPI.getBackgroundTypes(params);
      setBackgroundTypes(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, backgroundTypes: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load background types:', error);
    }
  };

  // New function to load bust colors
  const loadMannequinColors = async () => {
    try {
      const params = {
        page: paginationState.mannequinColors.page + 1,
        per_page: paginationState.mannequinColors.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.mannequinColors.orderBy,
        sort_order: sortingState.mannequinColors.order
      };
      const response = await masterDataConfigAPI.getBustColors(params);
      setMannequinColors(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, mannequinColors: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load bust colors:', error);
    }
  };

  // Function to load all bust colors for dropdowns (no pagination)
  const loadAllBustColors = async () => {
    try {
      console.log('Loading all bust colors for dropdown...');
      const response = await masterDataConfigAPI.getBustColors({ per_page: 100 }); // Get all bust colors (max 100 allowed by backend)
      // console.log('Bust colors response:', response.data);
      const bustColorsData = response.data.data || [];
      // console.log('Setting bust colors for dropdown:', bustColorsData);
      setBustColors(bustColorsData); // Use a separate state for dropdown data
    } catch (error) {
      console.error('Failed to load all bust colors for dropdown:', error);
    }
  };

  // Function to load all bust finishes for dropdowns (no pagination)
  const loadAllBustFinishes = async () => {
    try {
      console.log('Loading all bust finishes for dropdown...');
      const response = await masterDataConfigAPI.getBustFinishes({ per_page: 100 }); // Get all bust finishes (max 100 allowed by backend)
      // console.log('Bust finishes response:', response.data);
      const bustFinishesData = response.data.data || [];
      // console.log('Setting bust finishes for dropdown:', bustFinishesData);
      setBustFinishes(bustFinishesData); // Use a separate state for dropdown data
    } catch (error) {
      console.error('Failed to load all bust finishes for dropdown:', error);
    }
  };

  // New function to load gender options
  const loadGenderOptions = async () => {
    try {
      const params = {
        page: paginationState.genderOptions.page + 1,
        per_page: paginationState.genderOptions.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.genderOptions.orderBy,
        sort_order: sortingState.genderOptions.order
      };
      const response = await masterDataConfigAPI.getGenderOptions(params);
      setGenderOptions(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, genderOptions: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load gender options:', error);
    }
  };

  // New function to load skin tones
  const loadSkinTones = async () => {
    try {
      const params = {
        page: paginationState.skinTones.page + 1,
        per_page: paginationState.skinTones.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.skinTones.orderBy,
        sort_order: sortingState.skinTones.order
      };
      const response = await masterDataConfigAPI.getSkinTones(params);
      setSkinTones(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, skinTones: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load skin tones:', error);
    }
  };

  // New function to load ethnicities
  const loadEthnicities = async () => {
    try {
      const params = {
        page: paginationState.ethnicities.page + 1,
        per_page: paginationState.ethnicities.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.ethnicities.orderBy,
        sort_order: sortingState.ethnicities.order
      };
      const response = await masterDataConfigAPI.getEthnicities(params);
      setEthnicities(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, ethnicities: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load ethnicities:', error);
    }
  };

  // New function to load clothing types
  const loadClothingTypes = async () => {
    try {
      const params = {
        page: paginationState.clothingTypes.page + 1,
        per_page: paginationState.clothingTypes.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.clothingTypes.orderBy,
        sort_order: sortingState.clothingTypes.order
      };
      const response = await masterDataConfigAPI.getClothingTypes(params);
      setClothingTypes(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, clothingTypes: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load clothing types:', error);
    }
  };

  // New function to load bust finishes
  const loadBustFinishes = async () => {
    try {
      const params = {
        page: paginationState.bustFinishes.page + 1,
        per_page: paginationState.bustFinishes.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.bustFinishes.orderBy,
        sort_order: sortingState.bustFinishes.order
      };
      const response = await masterDataConfigAPI.getBustFinishes(params);
      setBustFinishes(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, bustFinishes: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load bust finishes:', error);
    }
  };

  // New function to load bust types
  const loadBustTypes = async () => {
    try {
      const params = {
        page: paginationState.bustTypes.page + 1,
        per_page: paginationState.bustTypes.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.bustTypes.orderBy,
        sort_order: sortingState.bustTypes.order
      };
      const response = await masterDataConfigAPI.getBustTypes(params);
      setBustTypes(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, bustTypes: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load bust types:', error);
    }
  };

  // New function to load app versions
  const loadAppVersions = async () => {
    try {
      console.log('🔍 Loading App Versions...', {
        page: paginationState.appVersions.page + 1,
        per_page: paginationState.appVersions.rowsPerPage,
        sort_by: sortingState.appVersions.orderBy,
        sort_order: sortingState.appVersions.order
      });

      const params = {
        page: paginationState.appVersions.page + 1,
        per_page: paginationState.appVersions.rowsPerPage,
        include_inactive: true, // Include inactive versions for management
        sort_by: sortingState.appVersions.orderBy,
        sort_order: sortingState.appVersions.order
      };

      const response = await appVersionAPI.getAppVersions(params);
      console.log('✅ App Versions API Response:', response.data);

      const appVersionsData = response.data.data || [];
      const totalCount = response.data.total || 0;

      console.log(`📊 Setting App Versions: ${appVersionsData.length} items, total: ${totalCount}`);

      setAppVersions(appVersionsData);
      setTotalCounts(prev => ({ ...prev, appVersions: totalCount }));

    } catch (error) {
      console.error('❌ Failed to load app versions:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  // New function to load media qualities
  const loadMediaQualities = async () => {
    try {
      const params = {
        page: paginationState.mediaQualities.page + 1,
        per_page: paginationState.mediaQualities.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.mediaQualities.orderBy,
        sort_order: sortingState.mediaQualities.order
      };
      const response = await masterDataConfigAPI.getMediaQualities(params);
      setMediaQualities(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, mediaQualities: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load media qualities:', error);
    }
  };

  const loadGenerationStatuses = async () => {
    try {
      const params = {
        page: paginationState.generationStatuses.page + 1,
        per_page: paginationState.generationStatuses.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.generationStatuses.orderBy,
        sort_order: sortingState.generationStatuses.order
      };
      const response = await masterDataConfigAPI.getGenerationStatuses(params);
      setGenerationStatuses(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, generationStatuses: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load generation status:', error);
    }
  };

  const loadCategoryMappings = async () => {
    try {
      const params = {
        page: paginationState.categoryMappings.page + 1,
        per_page: paginationState.categoryMappings.rowsPerPage,
        include_inactive: true, // Include inactive data by default, same as Model types
        sort_by: sortingState.categoryMappings.orderBy,
        sort_order: sortingState.categoryMappings.order
      };
      console.log('Loading category mappings with params:', params);
      const response = await masterDataConfigAPI.getCategoryMappings(params);
      console.log('Category mappings API response:', response.data);
      console.log('Category mappings data structure:', response.data.data);

      if (response.data.data && response.data.data.length > 0) {
        console.log('First mapping item:', response.data.data[0]);
        console.log('Available keys:', Object.keys(response.data.data[0]));
      }

      setCategoryMappings(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, categoryMappings: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load category mappings:', error);
    }
  };

  // Helper functions to load all data for dropdowns (not paginated)
  const loadAllCategories = async () => {
    try {
      console.log('Loading all categories for dropdown...');
      const response = await masterDataConfigAPI.getCategories({ per_page: 100 }); // Get all categories (max 100 allowed by backend)
      console.log('Categories response:', response.data);
      const categoriesData = response.data.data || [];
      console.log('Setting categories:', categoriesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load all categories:', error);
    }
  };

  const loadAllModelTypes = async () => {
    try {
      console.log('Loading all model types for dropdown...');
      const response = await masterDataConfigAPI.getModelTypes({ per_page: 100 }); // Get all model types (max 100 allowed by backend)
      console.log('Model types response:', response.data);
      const modelTypesData = response.data.data || [];
      console.log('Setting model types:', modelTypesData);
      setModelTypes(modelTypesData);
    } catch (error) {
      console.error('Failed to load all model types:', error);
    }
  };

  const loadRestrictedDomains = async () => {
    try {
      setLoading(true);
      const { page, rowsPerPage } = paginationState.restricted_email_domains;
      const { orderBy, order } = sortingState.restricted_email_domains;
      const response = await masterDataConfigAPI.getRestrictedDomains({
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: orderBy,
        sort_order: order,
        include_inactive: true,
      });
      setRestrictedDomains(response.data.data);
      setTotalCounts(prev => ({ ...prev, restricted_email_domains: response.data.total }));
    } catch (error) {
      console.error('Failed to load restricted domains:', error);
      setError('Failed to load restricted domains');
    } finally {
      setLoading(false);
    }
  };

  const loadIndustryTypes = async () => {
    try {
      const params = {
        page: paginationState.industryTypes.page + 1,
        per_page: paginationState.industryTypes.rowsPerPage,
        include_inactive: false,
        sort_by: sortingState.industryTypes.orderBy,
        sort_order: sortingState.industryTypes.order
      };

      const response = await masterDataConfigAPI.getIndustryTypes(params);

      if (response && response.data && Array.isArray(response.data.data)) {
        setIndustryTypes(response.data.data);
        setTotalCounts(prev => ({ ...prev, industryTypes: response.data.total || 0 }));
      } else {
        console.error('Invalid response format for industry types:', response);
        setIndustryTypes([]);
      }
    } catch (error) {
      console.error('Failed to load industry types:', error);
      setIndustryTypes([]);
    }
  };

  const loadCategoryModelMappings = async () => {
    try {
      const params = {
        page: paginationState.categoryModelMappings.page + 1,
        per_page: paginationState.categoryModelMappings.rowsPerPage,
        sort_by: sortingState.categoryModelMappings.orderBy,
        sort_order: sortingState.categoryModelMappings.order
      };
      console.log('Loading category model mappings with params:', params);
      const response = await masterDataConfigAPI.getCategoryModelMappings(params);
      console.log('Category model mappings API response:', response.data);
      console.log('Category model mappings data structure:', response.data.data);

      // Log sorting information
      console.log(`Received data sorted by: ${response.data.sort_by}, order: ${response.data.sort_order}`);

      setCategoryModelMappings(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, categoryModelMappings: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load category model mappings:', error);
    }
  };

  const loadCategoryGenderMappings = async () => {
    try {
      const params = {
        page: paginationState.categoryGenderMappings.page + 1,
        per_page: paginationState.categoryGenderMappings.rowsPerPage,
        sort_by: sortingState.categoryGenderMappings.orderBy,
        sort_order: sortingState.categoryGenderMappings.order
      };
      const response = await masterDataConfigAPI.getCategoryGenderMappings(params);
      setCategoryGenderMappings(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, categoryGenderMappings: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load category gender mappings:', error);
    }
  };

  const loadAddonServices = async () => {
    try {
      const { page, rowsPerPage } = paginationState.addonServices;
      const { orderBy, order } = sortingState.addonServices;
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: orderBy,
        sort_order: order
      };
      const response = await modelFaceAPI.getAddonServices(params);
      setAddonServices(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, addonServices: response.data.total || response.data.data.length || 0 }));
    } catch (error) {
      console.error('Failed to load addon services:', error);
      setError('Failed to load addon services');
    }
  };

  const loadThemes = async () => {
    try {
      const { page, rowsPerPage } = paginationState.themes;
      const { orderBy, order } = sortingState.themes;
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: orderBy,
        sort_order: order
      };
      const response = await masterDataConfigAPI.getThemes(params);
      setThemes(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, themes: response.data.total || response.data.data.length || 0 }));
    } catch (error) {
      console.error('Failed to load themes:', error);
      setError('Failed to load themes');
    }
  };

  const loadSystemSettings = async () => {
    try {
      const response = await masterDataConfigAPI.getSystemSettings();
      // console.log('System settings response:', response.data);

      if (response.data && response.data.settings) {
        // The response is grouped by category (CREDITS, BUSINESS, etc.), we need to flatten it
        // into a simple key-value object for easier access in the UI
        const categorizedSettings = response.data.settings;
        const flattenedSettings = {};

        // Flatten the categorized settings into a key-value object
        // This converts: { CREDITS: [{setting_key: "NEW_USER_CREDITS", ...}], ... }
        // Into: { NEW_USER_CREDITS: {setting_key: "NEW_USER_CREDITS", ...}, ... }
        Object.values(categorizedSettings).forEach(categorySettings => {
          if (Array.isArray(categorySettings)) {
            categorySettings.forEach(setting => {
              flattenedSettings[setting.setting_key] = setting;
            });
          }
        });

        // console.log('Flattened system settings:', flattenedSettings);

        // Log specific settings we're interested in
        if (flattenedSettings.NEW_USER_CREDITS) {
          // console.log('NEW_USER_CREDITS setting:', flattenedSettings.NEW_USER_CREDITS);
          // console.log('NEW_USER_CREDITS typed_value:', flattenedSettings.NEW_USER_CREDITS.typed_value);
        }
        if (flattenedSettings.LOW_CREDIT_THRESHOLD) {
          // console.log('LOW_CREDIT_THRESHOLD setting:', flattenedSettings.LOW_CREDIT_THRESHOLD);
          // console.log('LOW_CREDIT_THRESHOLD typed_value:', flattenedSettings.LOW_CREDIT_THRESHOLD.typed_value);
        }

        // Update state with the flattened settings
        setSystemSettings(flattenedSettings);
      } else {
        console.warn('No settings found in response');
        // console.log('Full response structure:', response.data);
        setSystemSettings({});
      }
    } catch (error) {
      // console.error('Failed to load system settings:', error);
      setSystemSettings({});
    }
  };

  // Load credit settings
  const loadCreditSettings = async () => {
    try {
      const response = await masterDataConfigAPI.getLatestCreditSetting();
      const creditSettings = response.data.credit_settings;
      if (creditSettings) {
        setFormData(prevState => ({
          ...prevState,
          image_cost: creditSettings.image_cost,
          gif_cost: creditSettings.gif_cost,
          video_cost: creditSettings.video_cost,
          watermark_cost: creditSettings.watermark_cost,
          brand_logo_cost: creditSettings.brand_logo_cost,
          credit_settings_id: creditSettings.id
        }));
      }
    } catch (error) {
      console.error('Failed to load credit settings:', error);
      setError('Failed to load credit settings');
    }
  };

  // Save credit settings
  // NOTE: This function updates BOTH the CreditSettings table AND the SystemSetting table
  // because the AI generation system reads from SystemSetting table (WATERMARK_COST, BRAND_LOGO_COST)
  // while the frontend displays values from CreditSettings table
  const handleSaveCreditSettings = async () => {
    try {
      console.log('💾 Starting to save credit settings...');
      console.log('📊 Form data:', formData);

      const creditSettingsData = {
        image_cost: formData.image_cost,
        gif_cost: formData.gif_cost,
        video_cost: formData.video_cost,
        watermark_cost: formData.watermark_cost,
        brand_logo_cost: formData.brand_logo_cost,
        is_active: true
      };

      console.log('📋 Credit settings data to save:', creditSettingsData);

      // First, update the CreditSettings table
      if (formData.credit_settings_id) {
        console.log('🔄 Updating existing credit settings with ID:', formData.credit_settings_id);
        // Update existing credit settings
        await masterDataConfigAPI.updateCreditSetting(formData.credit_settings_id, creditSettingsData);
        console.log('✅ CreditSettings table updated successfully');
      } else {
        console.log('🆕 Creating new credit settings');
        // Create new credit settings
        await masterDataConfigAPI.createCreditSetting(creditSettingsData);
        console.log('✅ CreditSettings table created successfully');
      }

      // Then, update the SystemSetting table for watermark and brand logo costs
      // This ensures the AI generation system uses the updated values
      try {
        console.log('🔧 Updating system settings...');
        console.log('💧 Updating WATERMARK_COST to:', formData.watermark_cost);
        await masterDataConfigAPI.updateSystemSetting('WATERMARK_COST', formData.watermark_cost);
        console.log('✅ WATERMARK_COST system setting updated successfully');

        console.log('🏷️ Updating BRAND_LOGO_COST to:', formData.brand_logo_cost);
        await masterDataConfigAPI.updateSystemSetting('BRAND_LOGO_COST', formData.brand_logo_cost);
        console.log('✅ BRAND_LOGO_COST system setting updated successfully');

        console.log('✅ All system settings updated successfully');
      } catch (systemSettingError) {
        console.warn('⚠️ Failed to update system settings:', systemSettingError);
        console.warn('⚠️ Error details:', systemSettingError.response?.data || systemSettingError.message);
        // Don't fail the entire operation if system settings update fails
        // The credit settings were still updated successfully
      }

      setSuccess('Credit settings saved successfully');
      console.log('🔄 Reloading credit settings...');
      await loadCreditSettings();
      console.log('🔄 Reloading system settings...');
      // Also reload system settings to reflect the changes
      await loadSystemSettings();
      console.log('✅ All data reloaded successfully');
    } catch (error) {
      console.error('❌ Failed to save credit settings:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      setError('Failed to save credit settings');
    }
  };

  // Generic preview image upload state (used for bust finishes, categories, ethnicities, clothing types)
  const [previewImageUploading, setPreviewImageUploading] = useState(false);
  const [previewImageError, setPreviewImageError] = useState('');
  // Keep aliases for backward compatibility with bust finish code
  const bustFinishPreviewUploading = previewImageUploading;
  const bustFinishPreviewError = previewImageError;

  // Generic helper function to construct proper image URL for preview images from any folder
  const getPreviewImageUrl = (imagePath, size = '400x400') => {
    if (!imagePath) return '';

    // Match uploads/<folder>/<filename> pattern for any allowed folder
    const folderMatch = imagePath.match(/^uploads\/(bust_finishes|categories|ethnicities|clothing_types|model_types|output_formats|gender_options|media_qualities|themes)\/(.+)$/);
    if (folderMatch) {
      const folder = folderMatch[1];
      const filename = folderMatch[2];
      // Gallery-style thumbnail query support from backend route.
      return `${API_URLS.BACKEND_BASE}/api/admin/master-data/uploads/${folder}/${filename}?size=${encodeURIComponent(size)}`;
    }

    return imagePath; // Return as-is if it doesn't match expected pattern
  };

  // Keep old name as alias for backward compatibility
  const getBustFinishImageUrl = getPreviewImageUrl;

  // Generic preview image upload handler for any master data type
  const handlePreviewImageUpload = async (file, folder, fieldName = 'preview_image_url') => {
    try {
      setPreviewImageError('');
      if (!file) return;

      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/webp',
        'image/svg+xml'
      ];
      if (!allowedTypes.includes(file.type)) {
        setPreviewImageError('Please select PNG, JPG, JPEG, SVG or WebP files.');
        return;
      }

      // 5MB limit
      if (file.size > 5 * 1024 * 1024) {
        setPreviewImageError('File size must be less than 5MB.');
        return;
      }

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', folder);

      setPreviewImageUploading(true);
      const response = await masterDataConfigAPI.uploadPreviewImage(formDataUpload);
      const uploadedPath = response?.data?.file_path;
      if (!uploadedPath) {
        setPreviewImageError('Upload succeeded but no file path returned.');
        return;
      }
      setFormData({ ...formData, [fieldName]: uploadedPath });
    } catch (uploadErr) {
      console.error(`Failed to upload preview image for ${folder}:`, uploadErr);
      const message = uploadErr?.response?.data?.error || 'Failed to upload preview';
      setPreviewImageError(message);
    } finally {
      setPreviewImageUploading(false);
    }
  };

  // Backward-compatible bust finish upload handler
  const handleBustFinishPreviewUpload = async (file) => {
    await handlePreviewImageUpload(file, 'bust_finishes', 'preview_image_url');
  };

  // Render form fields based on the type of data being edited/created
  const renderFormFields = (type) => {
    const commonFields = (
      <>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Name"
            value={formData.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            error={!!formErrors.name}
            helperText={formErrors.name || 'Enter a unique name (2-100 characters)'}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Display Name"
            value={formData.display_name || ''}
            onChange={(e) => handleFieldChange('display_name', e.target.value)}
            error={!!formErrors.display_name}
            helperText={formErrors.display_name || 'Enter a display name (2-100 characters)'}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            error={!!formErrors.description}
            helperText={formErrors.description || 'Optional description (max 500 characters)'}
            inputProps={{ maxLength: 500 }}
          />
        </Grid>
        {(type === 'categories' || type === 'materials') && (
          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.industry_type_id}>
              <InputLabel>Industry Type</InputLabel>
              <Select
                value={formData.industry_type_id || ''}
                onChange={(e) => handleFieldChange('industry_type_id', e.target.value || null)}
                label="Industry Type"
              >
                <MenuItem value="">
                  <em>Select Industry Type</em>
                </MenuItem>
                {industryTypes
                  .filter(it => it.is_active)
                  .map((industry) => (
                    <MenuItem key={industry.id} value={industry.id}>
                      {industry.display_name || industry.name}
                    </MenuItem>
                  ))}
              </Select>
              {formErrors.industry_type_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.industry_type_id}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Sort Order"
            value={formData.sort_order || 0}
            onChange={(e) => handleFieldChange('sort_order', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active !== undefined ? formData.is_active : true}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />
        </Grid>
      </>
    );

    switch (type) {
      case 'model_types':
      case 'media_qualities':
        return (
          <>
            {commonFields}
            {type === 'media_qualities' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.applies_to}>
                  <InputLabel id="media-quality-applies-to-label">Applies To</InputLabel>
                  <Select
                    labelId="media-quality-applies-to-label"
                    value={formData.applies_to || 'IMAGE'}
                    label="Applies To"
                    onChange={(e) => handleFieldChange('applies_to', e.target.value)}
                  >
                    <MenuItem value="IMAGE">Image / GIF output</MenuItem>
                    <MenuItem value="VIDEO">Video output</MenuItem>
                  </Select>
                  {formErrors.applies_to && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                      {formErrors.applies_to}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Image/GIF uses resolution options (e.g. 1K, 2K, 4K); Video uses height options (e.g. 480p, 720p).
                  </Typography>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Icon"
                  value={formData.icon || ''}
                  onChange={(e) => handleFieldChange('icon', e.target.value)}
                  placeholder="Click 'Select Icon' to choose an icon"
                  InputProps={{
                    readOnly: true,
                    startAdornment: formData.icon ? (
                      <InputAdornment position="start">
                        {(() => {
                          try {
                            const IconComponent = icons[formData.icon];
                            return IconComponent ? React.createElement(IconComponent) : null;
                          } catch (e) {
                            console.warn('Icon not found in form preview:', formData.icon);
                            return null;
                          }
                        })()}
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setIconPickerOpen(true)}
                  sx={{ minWidth: 120 }}
                >
                  Select Icon
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={previewImageUploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {previewImageUploading ? 'Uploading…' : `Upload ${type === 'model_types' ? 'Model Type' : 'Media Quality'} Image`}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  hidden
                  onChange={(e) => handlePreviewImageUpload(e.target.files?.[0], type, 'preview_image_url')}
                />
              </Button>
              {previewImageError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {previewImageError}
                </Typography>
              )}
              {formData.preview_image_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <SecureImage
                    src={getPreviewImageUrl(formData.preview_image_url)}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      console.error(`Failed to load ${type} preview in form:`, e.target.src);
                    }}
                  />
                </Box>
              )}
            </Grid>
          </>
        );

      case 'categories':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={previewImageUploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {previewImageUploading ? 'Uploading…' : 'Upload Category Image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  hidden
                  onChange={(e) => handlePreviewImageUpload(e.target.files?.[0], 'categories', 'icon_url')}
                />
              </Button>
              {previewImageError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {previewImageError}
                </Typography>
              )}
              {formData.icon_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <SecureImage
                    src={getPreviewImageUrl(formData.icon_url)}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      console.error('Failed to load category preview in form:', e.target.src);
                    }}
                  />
                </Box>
              )}
            </Grid>
          </>
        );

      case 'materials':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Preview Image URL"
                value={formData.preview_image_url || ''}
                onChange={(e) => handleFieldChange('preview_image_url', e.target.value)}
              />
            </Grid>
          </>
        );

      case 'output_formats':
        return (
          <>
            {commonFields}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Base Credits"
                value={formData.base_credits || 10}
                onChange={(e) => handleFieldChange('base_credits', parseInt(e.target.value) || 10)}
                error={!!formErrors.base_credits}
                helperText={formErrors.base_credits || 'Enter base credits (0-10000)'}
                inputProps={{ min: 0, max: 10000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Icon"
                  value={formData.icon || ''}
                  onChange={(e) => handleFieldChange('icon', e.target.value)}
                  placeholder="Click 'Select Icon' to choose an icon"
                  InputProps={{
                    readOnly: true,
                    startAdornment: formData.icon ? (
                      <InputAdornment position="start">
                        {(() => {
                          try {
                            const IconComponent = icons[formData.icon];
                            return IconComponent ? React.createElement(IconComponent) : null;
                          } catch (e) {
                            console.warn('Icon not found in form preview:', formData.icon);
                            return null;
                          }
                        })()}
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setIconPickerOpen(true)}
                  sx={{ minWidth: 120 }}
                >
                  Select Icon
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={previewImageUploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {previewImageUploading ? 'Uploading…' : 'Upload Output Format Image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  hidden
                  onChange={(e) => handlePreviewImageUpload(e.target.files?.[0], 'output_formats', 'preview_image_url')}
                />
              </Button>
              {previewImageError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {previewImageError}
                </Typography>
              )}
              {formData.preview_image_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <SecureImage
                    src={getPreviewImageUrl(formData.preview_image_url)}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      console.error('Failed to load output format preview in form:', e.target.src);
                    }}
                  />
                </Box>
              )}
            </Grid>
          </>
        );

      case 'gender_options':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Icon"
                  value={formData.icon || ''}
                  onChange={(e) => handleFieldChange('icon', e.target.value)}
                  placeholder="Click 'Select Icon' to choose an icon"
                  InputProps={{
                    readOnly: true,
                    startAdornment: formData.icon ? (
                      <InputAdornment position="start">
                        {(() => {
                          try {
                            const IconComponent = icons[formData.icon];
                            return IconComponent ? React.createElement(IconComponent) : null;
                          } catch (e) {
                            console.warn('Icon not found in form preview:', formData.icon);
                            return null;
                          }
                        })()}
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setIconPickerOpen(true)}
                  sx={{ minWidth: 120 }}
                >
                  Select Icon
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={previewImageUploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {previewImageUploading ? 'Uploading…' : 'Upload Gender Image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  hidden
                  onChange={(e) => handlePreviewImageUpload(e.target.files?.[0], 'gender_options', 'preview_image_url')}
                />
              </Button>
              {previewImageError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {previewImageError}
                </Typography>
              )}
              {formData.preview_image_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <SecureImage
                    src={getPreviewImageUrl(formData.preview_image_url)}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      console.error('Failed to load gender option preview in form:', e.target.src);
                    }}
                  />
                </Box>
              )}
            </Grid>
          </>
        );

      case 'bust_colors':
      case 'skin_tones':
        const PRESET_COLORS = [
          '#FFFFFF', '#000000', '#FF0000', '#00FF00', '#0000FF',
          '#FFFF00', '#00FFFF', '#FF00FF', '#C0C0C0', '#808080',
          '#800000', '#808000', '#008000', '#800080', '#008080',
          '#000080', '#F5F5DC', '#FFE4C4', '#D2B48C', '#8B4513'
        ];

        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Color Configuration
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  borderStyle: 'dashed'
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          borderRadius: '50%',
                          bgcolor: formData.hex_color || '#FFFFFF',
                          border: '4px solid #fff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                        onClick={() => document.getElementById('native-color-picker').click()}
                      >
                        <input
                          id="native-color-picker"
                          type="color"
                          value={formData.hex_color || '#ffffff'}
                          onChange={(e) => handleFieldChange('hex_color', e.target.value.toUpperCase())}
                          style={{
                            opacity: 0,
                            width: 0,
                            height: 0,
                            position: 'absolute'
                          }}
                        />
                        <Edit sx={{ color: (formData.hex_color?.toLowerCase() === '#ffffff' || !formData.hex_color) ? '#000' : '#fff', opacity: 0.8 }} />
                      </Box>
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                        Click circle to pick
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Hex Color Code"
                      value={formData.hex_color || ''}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value && !value.startsWith('#')) value = '#' + value;
                        handleFieldChange('hex_color', value.toUpperCase());
                      }}
                      error={!!formErrors.hex_color}
                      helperText={formErrors.hex_color || 'Standard Hex format (e.g., #FF5733)'}
                      placeholder="#FFFFFF"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">#</InputAdornment>,
                      }}
                      sx={{ mb: 2 }}
                    />

                    <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
                      Quick Presets:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {PRESET_COLORS.map(color => (
                        <Tooltip key={color} title={color} arrow>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '4px',
                              bgcolor: color,
                              cursor: 'pointer',
                              border: '1px solid rgba(0,0,0,0.1)',
                              boxShadow: formData.hex_color === color ? '0 0 0 2px #2196f3' : 'none',
                              '&:hover': {
                                transform: 'scale(1.2)',
                                zIndex: 1
                              },
                              transition: 'transform 0.1s'
                            }}
                            onClick={() => handleFieldChange('hex_color', color)}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Preview Image URL"
                value={formData.preview_image_url || ''}
                onChange={(e) => handleFieldChange('preview_image_url', e.target.value)}
                placeholder="https://example.com/image.jpg"
                helperText="Optional: Link to a visual preview of this color"
              />
            </Grid>
          </>
        );

      case 'ethnicities':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={previewImageUploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {previewImageUploading ? 'Uploading…' : 'Upload Ethnicity Image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  hidden
                  onChange={(e) => handlePreviewImageUpload(e.target.files?.[0], 'ethnicities', 'preview_image_url')}
                />
              </Button>
              {previewImageError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {previewImageError}
                </Typography>
              )}
              {formData.preview_image_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <SecureImage
                    src={getPreviewImageUrl(formData.preview_image_url)}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      console.error('Failed to load ethnicity preview in form:', e.target.src);
                    }}
                  />
                </Box>
              )}
            </Grid>
          </>
        );

      case 'clothing_types':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={previewImageUploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {previewImageUploading ? 'Uploading…' : 'Upload Clothing Type Image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  hidden
                  onChange={(e) => handlePreviewImageUpload(e.target.files?.[0], 'clothing_types', 'preview_image_url')}
                />
              </Button>
              {previewImageError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {previewImageError}
                </Typography>
              )}
              {formData.preview_image_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <SecureImage
                    src={getPreviewImageUrl(formData.preview_image_url)}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      console.error('Failed to load clothing type preview in form:', e.target.src);
                    }}
                  />
                </Box>
              )}
            </Grid>
          </>
        );

      case 'bust_finishes':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={bustFinishPreviewUploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {bustFinishPreviewUploading ? 'Uploading…' : 'Upload Preview Image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  hidden
                  onChange={(e) => handleBustFinishPreviewUpload(e.target.files?.[0])}
                />
              </Button>
              {bustFinishPreviewError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {bustFinishPreviewError}
                </Typography>
              )}
              {formData.preview_image_url && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <SecureImage
                    src={getBustFinishImageUrl(formData.preview_image_url)}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      console.error('Failed to load bust finish preview in form:', e.target.src);
                    }}
                    onLoad={() => {
                      console.log('Bust finish preview loaded in form:', getBustFinishImageUrl(formData.preview_image_url));
                    }}
                  />
                </Box>
              )}
            </Grid>
          </>
        );

      case 'core_materials':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Preview Image URL"
                value={formData.preview_image_url || ''}
                onChange={(e) => handleFieldChange('preview_image_url', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>
          </>
        );



      case 'generation_statuses':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Color Code"
                value={formData.color_code || ''}
                onChange={(e) => handleFieldChange('color_code', e.target.value)}
                error={!!formErrors.color_code}
                helperText={formErrors.color_code || 'Enter a valid hex color code (e.g., #FF0000)'}
                placeholder="#FFFFFF"
              />
            </Grid>
          </>
        );

      case 'industry_types':
      case 'model_types':
      case 'background_types':
        return commonFields;

      case 'category_mappings':
        return (
          <>
            {/* Category Field - Required */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.category_id}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id || ''}
                  onChange={(e) => handleFieldChange('category_id', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories
                    .filter(cat => cat.is_active)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.display_name || category.name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.category_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.category_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Model Type Field - Required */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.model_type_id}>
                <InputLabel>Model Type</InputLabel>
                <Select
                  value={formData.model_type_id || ''}
                  onChange={(e) => handleFieldChange('model_type_id', e.target.value)}
                  label="Model Type"
                >
                  <MenuItem value="">
                    <em>Select Model Type</em>
                  </MenuItem>
                  {modelTypes
                    .filter(mt => mt.is_active)
                    .map((modelType) => (
                      <MenuItem key={modelType.id} value={modelType.id}>
                        {modelType.display_name || modelType.name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.model_type_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.model_type_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>



            {/* Show message when no model type is selected */}
            {!formData.model_type_id && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontStyle: 'italic' }}>
                  Please select a Model Type to see available attributes
                </Typography>
              </Grid>
            )}

            {/* Human Model Fields - Only show when Human model is selected */}
            {(() => {
              const selectedModelType = modelTypes.find(mt => mt.id === formData.model_type_id);
              const isHumanModel = selectedModelType && (
                selectedModelType.name.toLowerCase().includes('human') ||
                selectedModelType.display_name.toLowerCase().includes('human')
              );

              if (isHumanModel) {
                return (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '1px solid', pb: 1 }}>
                        🧑 Human Model Attributes
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Ethnicity *</InputLabel>
                        <Select
                          value={formData.ethnicity_id || ''}
                          onChange={(e) => handleFieldChange('ethnicity_id', e.target.value || null)}
                          label="Ethnicity *"
                        >
                          <MenuItem value="">
                            <em>Select Ethnicity (Required)</em>
                          </MenuItem>
                          {ethnicities
                            .filter(eth => eth.is_active)
                            .map((ethnicity) => (
                              <MenuItem key={ethnicity.id} value={ethnicity.id}>
                                {ethnicity.display_name || ethnicity.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Clothing Type (Optional)</InputLabel>
                        <Select
                          value={formData.clothing_type_id || ''}
                          onChange={(e) => handleFieldChange('clothing_type_id', e.target.value || null)}
                          label="Clothing Type (Optional)"
                        >
                          <MenuItem value="">
                            <em>None - Hide in AI Generation</em>
                          </MenuItem>
                          {clothingTypes
                            .filter(ct => ct.is_active)
                            .map((clothingType) => (
                              <MenuItem key={clothingType.id} value={clothingType.id}>
                                {clothingType.display_name || clothingType.name}
                              </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2 }}>
                          Leave empty to hide Clothing Type dropdown in AI Generation for this category
                        </Typography>
                      </FormControl>
                    </Grid>
                  </>
                );
              }
              return null;
            })()}

            {/* Bust Model Fields - Only show when Bust model is selected */}
            {(() => {
              const selectedModelType = modelTypes.find(mt => mt.id === formData.model_type_id);
              const isBustModel = selectedModelType && (
                selectedModelType.name.toLowerCase().includes('bust') ||
                selectedModelType.display_name.toLowerCase().includes('bust')
              );

              if (isBustModel) {
                return (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '1px solid', pb: 1 }}>
                        🎭 Bust Model Attributes
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Bust Finish *</InputLabel>
                        <Select
                          value={formData.bust_finish_id || ''}
                          onChange={(e) => handleFieldChange('bust_finish_id', e.target.value || null)}
                          label="Bust Finish *"
                        >
                          <MenuItem value="">
                            <em>Select Bust Finish (Required)</em>
                          </MenuItem>
                          {bustFinishes
                            .filter(bf => bf.is_active)
                            .map((bustFinish) => (
                              <MenuItem key={bustFinish.id} value={bustFinish.id}>
                                {bustFinish.display_name || bustFinish.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Bust Color *</InputLabel>
                        <Select
                          value={formData.bust_color_id || ''}
                          onChange={(e) => handleFieldChange('bust_color_id', e.target.value || null)}
                          label="Bust Color *"
                        >
                          <MenuItem value="">
                            <em>Select Bust Color (Required)</em>
                          </MenuItem>
                          {bustColors
                            .filter(bc => bc.is_active)
                            .map((bustColor) => (
                              <MenuItem key={bustColor.id} value={bustColor.id}>
                                {bustColor.display_name || bustColor.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                );
              }
              return null;
            })()}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={formData.sort_order || 0}
                onChange={(e) => handleFieldChange('sort_order', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active !== undefined ? formData.is_active : true}
                    onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </>
        );

      case 'category_model_mappings':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.category_id}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id || ''}
                  onChange={(e) => handleFieldChange('category_id', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories
                    .filter(cat => cat.is_active)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.display_name || category.name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.category_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.category_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.model_type_id}>
                <InputLabel>Model Type</InputLabel>
                <Select
                  value={formData.model_type_id || ''}
                  onChange={(e) => handleFieldChange('model_type_id', e.target.value)}
                  label="Model Type"
                >
                  <MenuItem value="">
                    <em>Select Model Type</em>
                  </MenuItem>
                  {modelTypes
                    .filter(mt => mt.is_active)
                    .map((modelType) => (
                      <MenuItem key={modelType.id} value={modelType.id}>
                        {modelType.display_name || modelType.name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.model_type_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.model_type_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={formData.sort_order || 0}
                onChange={(e) => handleFieldChange('sort_order', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active !== undefined ? formData.is_active : true}
                    onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </>
        );
      case 'category_gender_mappings':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.category_id}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id || ''}
                  onChange={(e) => handleFieldChange('category_id', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories
                    .filter(cat => cat.is_active)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.display_name || category.name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.category_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.category_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.gender_option_id}>
                <InputLabel>Gender Option</InputLabel>
                <Select
                  value={formData.gender_option_id || ''}
                  onChange={(e) => handleFieldChange('gender_option_id', e.target.value)}
                  label="Gender Option"
                >
                  <MenuItem value="">
                    <em>Select Gender Option</em>
                  </MenuItem>
                  {genderOptions
                    .filter(go => go.is_active)
                    .map((genderOption) => (
                      <MenuItem key={genderOption.id} value={genderOption.id}>
                        {genderOption.display_name || genderOption.name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.gender_option_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.gender_option_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={formData.sort_order || 0}
                onChange={(e) => handleFieldChange('sort_order', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active !== undefined ? formData.is_active : true}
                    onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </>
        );

      case 'app_versions':
        return (
          <>
            {/* Android Version Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '1px solid', pb: 1 }}>
                🤖 Android App Version
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Android Version Code"
                value={formData.android_version_code || ''}
                onChange={(e) => handleFieldChange('android_version_code', e.target.value)}
                error={!!formErrors.android_version_code}
                helperText={formErrors.android_version_code || 'Internal version number (e.g., 1, 2, 3)'}
                placeholder="1"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Android Version Name"
                value={formData.android_version_name || ''}
                onChange={(e) => handleFieldChange('android_version_name', e.target.value)}
                error={!!formErrors.android_version_name}
                helperText={formErrors.android_version_name || 'Public version name (e.g., 1.0.0, 2.1.0)'}
                placeholder="1.0.0"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Android Release Notes"
                value={formData.android_release_note || ''}
                onChange={(e) => handleFieldChange('android_release_note', e.target.value)}
                placeholder="What's new in this version..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Android Update URL"
                value={formData.android_update_redirection_url || ''}
                onChange={(e) => handleFieldChange('android_update_redirection_url', e.target.value)}
                placeholder="https://play.google.com/store/apps/details?id=com.glammify.app"
                helperText="URL where users can download/update the app"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Android Deep Link Schema"
                value={formData.android_deep_link_schema || ''}
                onChange={(e) => handleFieldChange('android_deep_link_schema', e.target.value)}
                placeholder="glammify://"
                helperText="Custom URL scheme for deep linking"
              />
            </Grid>

            {/* iOS Version Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '1px solid', pb: 1 }}>
                🍎 iOS App Version
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="iOS Version Code"
                value={formData.ios_version_code || ''}
                onChange={(e) => handleFieldChange('ios_version_code', e.target.value)}
                error={!!formErrors.ios_version_code}
                helperText={formErrors.ios_version_code || 'Internal version number (e.g., 1, 2, 3)'}
                placeholder="1"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="iOS Version Name"
                value={formData.ios_version_name || ''}
                onChange={(e) => handleFieldChange('ios_version_name', e.target.value)}
                error={!!formErrors.ios_version_name}
                helperText={formErrors.ios_version_name || 'Public version name (e.g., 1.0.0, 2.1.0)'}
                placeholder="1.0.0"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="iOS Release Notes"
                value={formData.ios_release_note || ''}
                onChange={(e) => handleFieldChange('ios_release_note', e.target.value)}
                placeholder="What's new in this version..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="iOS Update URL"
                value={formData.ios_update_redirection_url || ''}
                onChange={(e) => handleFieldChange('ios_update_redirection_url', e.target.value)}
                placeholder="https://apps.apple.com/us/app/glammify/id6742479664"
                helperText="URL where users can download/update the app"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="iOS Deep Link Schema"
                value={formData.ios_deep_link_schema || ''}
                onChange={(e) => handleFieldChange('ios_deep_link_schema', e.target.value)}
                placeholder="glammify://"
                helperText="Custom URL scheme for deep linking"
              />
            </Grid>

            {/* Universal Links Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '1px solid', pb: 1 }}>
                🔗 Universal Links & App Links
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Universal Link Domain"
                value={formData.universal_link_domain || ''}
                onChange={(e) => handleFieldChange('universal_link_domain', e.target.value)}
                placeholder="example.com"
                helperText="Domain for iOS universal links"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Android App Link Domain"
                value={formData.android_app_link_domain || ''}
                onChange={(e) => handleFieldChange('android_app_link_domain', e.target.value)}
                placeholder="example.com"
                helperText="Domain for Android app links"
              />
            </Grid>

            {/* Configuration Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '1px solid', pb: 1 }}>
                ⚙️ Configuration
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Brief description of this version..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Release Date"
                value={formData.release_date || ''}
                onChange={(e) => handleFieldChange('release_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Required Version"
                value={formData.min_required_version || ''}
                onChange={(e) => handleFieldChange('min_required_version', e.target.value)}
                placeholder="1.0.0"
                helperText="Minimum version required for app to work"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enable_deep_linking !== undefined ? formData.enable_deep_linking : true}
                    onChange={(e) => handleFieldChange('enable_deep_linking', e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Deep Linking"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active !== undefined ? formData.is_active : false}
                    onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active Version"
                helperText="Only one version can be active at a time"
              />
            </Grid>
          </>
        );

      case 'themes':
        return (
          <>
            {commonFields}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Theme Icon"
                  value={formData.icon || ''}
                  onChange={(e) => handleFieldChange('icon', e.target.value)}
                  placeholder="Click 'Select Icon' to choose an icon for this theme"
                  InputProps={{
                    readOnly: true,
                    startAdornment: formData.icon ? (
                      <InputAdornment position="start">
                        {(() => {
                          try {
                            const IconComponent = icons[formData.icon];
                            return IconComponent ? React.createElement(IconComponent) : null;
                          } catch (e) {
                            console.warn('Icon not found in form preview:', formData.icon);
                            return null;
                          }
                        })()}
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setIconPickerOpen(true)}
                  sx={{ minWidth: 120 }}
                >
                  Select Icon
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview Image URL</Typography>
                <TextField
                  fullWidth
                  label="Preview Image URL"
                  value={formData.preview_image_url || ''}
                  onChange={(e) => handleFieldChange('preview_image_url', e.target.value)}
                  placeholder="https://example.com/image.png"
                  helperText="Optional URL for the theme preview image"
                />
              </Box>

              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1, textAlign: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="theme-preview-upload"
                  type="file"
                  onChange={(e) => handlePreviewImageUpload(e.target.files?.[0], 'themes', 'preview_image_url')}
                />
                <label htmlFor="theme-preview-upload">
                  <Button variant="outlined" component="span" startIcon={<Upload />}>
                    Upload Preview Image
                  </Button>
                </label>
                {formData.preview_image_url && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={getPreviewImageUrl(formData.preview_image_url)}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Invalid+URL'; }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </>
        );

      case 'restricted_email_domains':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Domain"
                value={formData.domain || ''}
                onChange={(e) => handleFieldChange('domain', e.target.value)}
                error={!!formErrors.domain}
                helperText={formErrors.domain || 'Enter the domain to restrict (e.g., example.com)'}
              />
            </Grid>
          </>
        );

      case 'addonServices':
        return (
          <>
            {commonFields}
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Service Code"
                value={formData.code || ''}
                onChange={(e) => handleFieldChange('code', e.target.value)}
                error={!!formErrors.code}
                helperText={formErrors.code || 'Unique service identifier (e.g., model_face)'}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Icon Name"
                  value={formData.icon_name || ''}
                  onChange={(e) => handleFieldChange('icon_name', e.target.value)}
                  placeholder="Click 'Select Icon' to choose"
                  InputProps={{
                    readOnly: true,
                    startAdornment: formData.icon_name ? (
                      <InputAdornment position="start">
                        {(() => {
                          try {
                            const IconComponent = icons[formData.icon_name];
                            return IconComponent ? React.createElement(IconComponent) : null;
                          } catch (e) {
                            return null;
                          }
                        })()}
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Temporarily mapping icon_name to icon for IconPicker compatibility
                    setFormData(prev => ({ ...prev, icon: prev.icon_name }));
                    setIconPickerOpen(true);
                  }}
                  sx={{ minWidth: 120 }}
                >
                  Select Icon
                </Button>
              </Box>
            </Grid>
          </>
        );

      default:
        return commonFields;
    }
  };

  // Helper function to get tab key from dialog type
  const getTabKey = (dialogType) => {
    switch (dialogType) {
      case 'model_types': return 'modelTypes';
      case 'output_formats': return 'outputFormats';
      case 'categories': return 'categories';
      case 'materials': return 'materials';
      case 'background_types': return 'backgroundTypes';
      case 'bust_colors': return 'mannequinColors';
      case 'gender_options': return 'genderOptions';
      case 'skin_tones': return 'skinTones'; // New: Add skin tones tab key
      case 'ethnicities': return 'ethnicities'; // New: Add ethnicities tab key
      case 'clothing_types': return 'clothingTypes'; // New: Add clothing types tab key
      case 'bust_finishes': return 'bustFinishes'; // New: Add bust finishes tab key
      case 'media_qualities': return 'mediaQualities'; // New: Add media qualities tab key
      case 'generation_statuses': return 'generationStatuses';
      case 'industry_types': return 'industryTypes';
      case 'category_mappings': return 'categoryMappings';
      case 'category_model_mappings': return 'categoryModelMappings'; // Add category model mappings tab key
      case 'bust_types': return 'bustTypes'; // New: Add bust types tab key
      case 'app_versions': return 'appVersions'; // New: Add app versions tab key
      case 'category_gender_mappings': return 'categoryGenderMappings';
      case 'restricted_email_domains': return 'restricted_email_domains';
      case 'addonServices': return 'addonServices'; // New: Add addon services tab key
      case 'themes': return 'themes'; // New: Add themes tab key
      case 'system_settings': return 'systemSettings';
      default: return '';
    }
  };

  // Helper function to get current data for a tab type
  const getCurrentData = (type) => {
    switch (type) {
      case 'model_types': return modelTypes;
      case 'output_formats': return outputFormats;
      case 'categories': return categories;
      case 'materials': return materials;
      case 'background_types': return backgroundTypes;
      case 'bust_colors': return mannequinColors;
      case 'gender_options': return genderOptions;
      case 'skin_tones': return skinTones; // New: Add skin tones to current data
      case 'ethnicities': return ethnicities; // New: Add ethnicities to current data
      case 'clothing_types': return clothingTypes; // New: Add clothing types to current data
      case 'bust_finishes': return bustFinishes; // New: Add bust finishes to current data
      case 'media_qualities': return mediaQualities; // New: Add media qualities to current data
      case 'generation_statuses': return generationStatuses;
      case 'industry_types': return industryTypes;
      case 'category_mappings': return categoryMappings;
      case 'category_model_mappings': return categoryModelMappings; // Add category model mappings to current data
      case 'bust_types': return bustTypes; // New: Add bust types to current data
      case 'app_versions': return appVersions; // New: Add app versions to current data
      case 'category_gender_mappings': return categoryGenderMappings;
      case 'restricted_email_domains': return restrictedDomains;
      case 'addonServices': return addonServices; // New: Add addon services data
      case 'themes': return themes; // New: Add themes to current data
      default: return [];
    }
  };

  const handleCreate = async () => {
    if (!canCreate) {
      setError('You do not have permission to create items');
      return;
    }

    // Validate form data
    const validationErrors = validateForm(formData, dialogType);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    try {
      const apiMethod = getCreateMethod(dialogType);

      // Clean form data for category mappings - convert empty strings to null
      let cleanedFormData = { ...formData };
      if (dialogType === 'category_mappings') {
        cleanedFormData = cleanCategoryMappingData(formData);

        // Validate the cleaned data
        const categoryMappingErrors = validateCategoryMappingData(cleanedFormData);
        if (categoryMappingErrors.length > 0) {
          setError(`Validation failed: ${categoryMappingErrors.join(', ')}`);
          return;
        }
      }

      await apiMethod(cleanedFormData);
      handleDialogClose(); // Use the centralized cleanup function

      // Navigate to first page to show new item
      const tabKey = getTabKey(dialogType);
      setPaginationState(prev => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], page: 0 }
      }));

      loadAllData();
      setSuccess('Record created successfully');
    } catch (error) {
      console.error('Failed to create item:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.error) || `Failed to create ${dialogType.replace('_', ' ')}`;
      setError(errorMessage);
      setDialogOpen(false); // Close dialog when there's an error
    }
  };

  const handleUpdate = async () => {
    if (!canUpdate) {
      setError('You do not have permission to update items');
      return;
    }

    // Validate form data
    const validationErrors = validateForm(formData, dialogType);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    try {
      const apiMethod = getUpdateMethod(dialogType);

      // Clean form data for category mappings - convert empty strings to null
      let cleanedFormData = { ...formData };
      if (dialogType === 'category_mappings') {
        cleanedFormData = cleanCategoryMappingData(formData);

        // Validate the cleaned data
        const categoryMappingErrors = validateCategoryMappingData(cleanedFormData);
        if (categoryMappingErrors.length > 0) {
          setError(`Validation failed: ${categoryMappingErrors.join(', ')}`);
          return;
        }
      }

      await apiMethod(editingItem.id, cleanedFormData);
      handleDialogClose(); // Use the centralized cleanup function

      // Reload current page to show updated item
      loadAllData();
      setSuccess('Record updated successfully');
    } catch (error) {
      console.error('Failed to update item:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.error) || `Failed to update ${dialogType.replace('_', ' ')}`;
      setError(errorMessage);
      setDialogOpen(false); // Close dialog when there's an error
    }
  };

  const handleDelete = (item, type) => {
    setItemToDelete({ item, type });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      setError('You do not have permission to delete items');
      return;
    }

    if (itemToDelete) {
      try {
        const apiMethod = getDeleteMethod(itemToDelete.type);
        await apiMethod(itemToDelete.item.id);
        setDeleteDialogOpen(false);

        // Get current data and pagination state for this tab
        const tabKey = getTabKey(itemToDelete.type);
        const currentData = getCurrentData(itemToDelete.type);
        const currentPagination = paginationState[tabKey];

        // If current page becomes empty after deletion, go to previous page
        if (currentData.length === 1 && currentPagination.page > 0) {
          setPaginationState(prev => ({
            ...prev,
            [tabKey]: { ...prev[tabKey], page: currentPagination.page - 1 }
          }));
        } else {
          loadAllData();
        }

        setItemToDelete(null);
        setSuccess('Record deleted successfully');
      } catch (error) {
        console.error('Failed to delete item:', error);
        const errorMessage = (error.response && error.response.data && error.response.data.error) || `Failed to delete ${itemToDelete.type.replace('_', ' ')}`;
        setError(errorMessage);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleEdit = async (item, type) => {
    setEditingItem(item);
    setDialogType(type); // Set the dialog type for editing
    setFormErrors({}); // Clear form validation errors
    setError(''); // Clear any previous errors

    // Load required data for category mappings
    if (type === 'category_mappings') {
      try {
        // Ensure all required data is loaded for the dropdowns
        await Promise.all([
          loadAllCategories(),
          loadAllModelTypes(),
          loadMaterials(),
          loadEthnicities(),
          loadClothingTypes(),
          loadBustFinishes(),
          loadMannequinColors()
        ]);
      } catch (error) {
        console.error('Failed to load dropdown data for category mappings:', error);
      }
    }

    // Load model types for bust types
    if (type === 'bust_types') {
      try {
        // Ensure model types are loaded for the dropdown
        await loadAllModelTypes();

        // Find the BUST model type
        const bustModelType = modelTypes.find(mt => mt.name === 'BUST');
        if (bustModelType && (!item.model_type_id || item.model_type_id !== bustModelType.id)) {
          // Set the BUST model type as the default if not already set
          item.model_type_id = bustModelType.id;
        }
      } catch (error) {
        console.error('Failed to load model types for bust types:', error);
      }
    }

    // Prepare form data with proper handling for industry_type_id
    const formData = {
      ...item,
      industry_type_id: item.industry_type_id || ''
    };

    // Media quality: scope for wizard filtering (IMAGE vs VIDEO)
    if (type === 'media_qualities') {
      formData.applies_to = item.applies_to || 'IMAGE';
    }

    // For categories and materials, ensure industry_type_id is properly set
    if (type === 'categories' || type === 'materials') {
      formData.industry_type_id = item.industry_type_id || '';
    }

    // For app versions, format the release date for the date input
    if (type === 'app_versions' && item.release_date) {
      try {
        const date = new Date(item.release_date);
        if (!isNaN(date.getTime())) {
          formData.release_date = date.toISOString().split('T')[0];
        }
      } catch (e) {
        console.warn('Could not parse release date:', item.release_date);
      }
    }

    setFormData(formData);
    setDialogOpen(true);
  };

  const handleNew = async (type) => {
    console.log('handleNew called with type:', type);
    setDialogType(type);
    setEditingItem(null);
    setFormErrors({}); // Clear form validation errors
    setError(''); // Clear any previous errors

    if (type === 'category_mappings') {
      console.log('Loading data for category_mappings...');
      try {
        // Ensure all required data is loaded for the dropdowns
        await Promise.all([
          loadAllCategories(),
          loadAllModelTypes(),
          loadMaterials(),
          loadEthnicities(),
          loadClothingTypes(),
          loadAllBustFinishes(),
          loadAllBustColors()
        ]);
        console.log('Finished loading dropdown data for category mappings');

        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 100));

      } catch (error) {
        console.error('Failed to load dropdown data for category mappings:', error);
      }

      setFormData({
        model_type_id: '',
        ethnicity_id: '',
        clothing_type_id: '',
        bust_finish_id: '',
        bust_color_id: '',
        is_active: true,
        sort_order: 0
      });
    } else if (type === 'bust_colors' || type === 'skin_tones') {
      setFormData({
        name: '',
        display_name: '',
        description: '',
        hex_color: '',
        preview_image_url: '',
        is_active: true,
        sort_order: 0
      });
    } else if (type === 'app_versions') {
      setFormData({
        // App version specific fields
        android_version_code: '',
        android_release_note: '',
        android_version_name: '',
        android_update_redirection_url: '',
        android_deep_link_schema: '',
        ios_version_code: '',
        ios_release_note: '',
        ios_version_name: '',
        ios_update_redirection_url: '',
        ios_deep_link_schema: '',
        universal_link_domain: '',
        android_app_link_domain: '',
        enable_deep_linking: false,
        description: '',
        release_date: '',
        min_required_version: '',
        is_active: false,
        // Common fields (not used in form but needed for validation)
        name: '',
        display_name: ''
      });
    } else if (type === 'restricted_email_domains') {
      setFormData({
        domain: '',
        is_active: true
      });
    } else if (type === 'media_qualities') {
      setFormData({
        name: '',
        display_name: '',
        description: '',
        icon: '',
        preview_image_url: '',
        is_active: true,
        sort_order: 0,
        applies_to: 'IMAGE',
        metadata: {}
      });
    } else {
      setFormData({
        name: '',
        display_name: '',
        description: '',
        icon: '',
        is_active: true,
        sort_order: 0,
        industry_type_id: '',
        metadata: {}
      });
    }
    console.log('Opening dialog...');
    setDialogOpen(true);
  };

  const handleSystemSettingUpdate = async (key, value) => {
    if (!canUpdate) {
      setError('You do not have permission to update system settings');
      return;
    }

    try {
      await masterDataConfigAPI.updateSystemSetting(key, value);
      loadSystemSettings();
      setSuccess('System setting updated successfully');
    } catch (error) {
      console.error('Failed to update system setting:', error);
      setError('Failed to update system setting');
    }
  };

  const handleSaveSystemSettings = async () => {
    if (!canUpdate) {
      setError('You do not have permission to update system settings');
      return;
    }

    try {
      setLoading(true);

      // Get current values from the form fields
      const newUserCredits = systemSettings.NEW_USER_CREDITS?.typed_value ?? systemSettings.NEW_USER_CREDITS?.setting_value ?? 1000;
      const lowCreditThreshold = systemSettings.LOW_CREDIT_THRESHOLD?.typed_value ?? systemSettings.LOW_CREDIT_THRESHOLD?.setting_value ?? 500;

      // Save NEW_USER_CREDITS setting
      await masterDataConfigAPI.updateSystemSetting('NEW_USER_CREDITS', newUserCredits);

      // Save LOW_CREDIT_THRESHOLD setting
      await masterDataConfigAPI.updateSystemSetting('LOW_CREDIT_THRESHOLD', lowCreditThreshold);

      // Update local state immediately with the saved values
      setSystemSettings(prev => ({
        ...prev,
        NEW_USER_CREDITS: {
          ...prev.NEW_USER_CREDITS,
          typed_value: newUserCredits,
          setting_value: newUserCredits.toString()
        },
        LOW_CREDIT_THRESHOLD: {
          ...prev.LOW_CREDIT_THRESHOLD,
          typed_value: lowCreditThreshold,
          setting_value: lowCreditThreshold.toString()
        }
      }));

      // Also reload from database to ensure consistency
      // Add a small delay to ensure the database has been updated
      setTimeout(async () => {
        await loadSystemSettings();
      }, 500);

      setSuccess('System configuration saved successfully');
    } catch (error) {
      console.error('Failed to save system settings:', error);
      setError('Failed to save system configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!canUpdate) {
      setError('You do not have permission to reset system settings');
      return;
    }

    // Open confirmation dialog instead of using window.confirm
    setResetDialogOpen(true);
  };

  // Handle Initialize System Settings
  const handleInitializeSystemSettings = async () => {
    if (!canUpdate) {
      return;
    }

    try {
      // Call the initialize API endpoint
      await masterDataConfigAPI.initialize();

      // Reload credit settings to reflect the initialized values
      await loadCreditSettings();
    } catch (error) {
      console.error('Failed to initialize system settings:', error);
      // Silently fail - don't show error to user for background initialization
    }
  };

  const handleConfirmReset = async () => {
    try {
      setLoading(true);
      // Reset to defaults: base costs (image, gif, video) come from environment variables,
      // while watermark and brand logo costs come from system settings
      await masterDataConfigAPI.resetToDefaults();
      await loadSystemSettings();
      await loadCreditSettings();
      setSuccess('System settings reset to defaults');
    } catch (error) {
      console.error('Failed to reset settings:', error);
      setError('Failed to reset system settings');
    } finally {
      setLoading(false);
      setResetDialogOpen(false);
    }
  };

  const handleCancelReset = () => {
    setResetDialogOpen(false);
  };

  // Handle dialog close - clear form data and editing state
  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      icon: '',
      is_active: true,
      sort_order: 0,
      metadata: {},
      preview_image_url: '',
      hex_color: '',
      model_type_id: '',
      ethnicity_id: '',
      clothing_type_id: '',
      bust_finish_id: '',
      bust_color_id: '',
      // App version fields
      android_version_code: '',
      android_release_note: '',
      android_version_name: '',
      android_update_redirection_url: '',
      android_deep_link_schema: '',
      ios_version_code: '',
      ios_release_note: '',
      ios_version_name: '',
      ios_update_redirection_url: '',
      ios_deep_link_schema: '',
      universal_link_domain: '',
      android_app_link_domain: '',
      enable_deep_linking: false,
      description: '',
      release_date: '',
      min_required_version: ''
    });
    setFormErrors({}); // Clear form validation errors
    setError(''); // Clear any error messages
  };

  const getCreateMethod = (type) => {
    switch (type) {
      case 'model_types': return masterDataConfigAPI.createModelType;
      case 'output_formats': return masterDataConfigAPI.createOutputFormat;
      case 'categories': return masterDataConfigAPI.createCategory;
      case 'materials': return masterDataConfigAPI.createMaterial;
      case 'background_types': return masterDataConfigAPI.createBackgroundType;
      case 'bust_colors': return masterDataConfigAPI.createBustColor;
      case 'gender_options': return masterDataConfigAPI.createGenderOption;
      case 'skin_tones': return masterDataConfigAPI.createSkinTone; // New: Add skin tone create method
      case 'ethnicities': return masterDataConfigAPI.createEthnicity; // New: Add ethnicity create method
      case 'clothing_types': return masterDataConfigAPI.createClothingType; // New: Add clothing type create method
      case 'bust_finishes': return masterDataConfigAPI.createBustFinish; // New: Add bust finish create method
      case 'media_qualities': return masterDataConfigAPI.createMediaQuality; // New: Add media quality create method
      case 'generation_statuses': return masterDataConfigAPI.createGenerationStatus;
      case 'industry_types': return masterDataConfigAPI.createIndustryType;
      case 'category_mappings': return masterDataConfigAPI.createCategoryMapping;
      case 'category_model_mappings': return masterDataConfigAPI.createCategoryModelMapping; // Add category model mapping create method
      case 'credit_settings': return masterDataConfigAPI.createCreditSetting;
      case 'bust_types': return masterDataConfigAPI.createBustType; // New: Add bust type create method
      case 'app_versions': return appVersionAPI.createAppVersion; // New: Add app version create method
      case 'category_gender_mappings': return masterDataConfigAPI.createCategoryGenderMapping;
      case 'restricted_email_domains': return masterDataConfigAPI.createRestrictedDomain;
      case 'addonServices': return modelFaceAPI.createAddonService; // New: Add addon service create method
      case 'themes': return masterDataConfigAPI.createTheme; // New: Add theme create method
      default: return null;
    }
  };

  const getUpdateMethod = (type) => {
    switch (type) {
      case 'model_types': return masterDataConfigAPI.updateModelType;
      case 'output_formats': return masterDataConfigAPI.updateOutputFormat;
      case 'categories': return masterDataConfigAPI.updateCategory;
      case 'materials': return masterDataConfigAPI.updateMaterial;
      case 'background_types': return masterDataConfigAPI.updateBackgroundType;
      case 'bust_colors': return masterDataConfigAPI.updateBustColor;
      case 'gender_options': return masterDataConfigAPI.updateGenderOption;
      case 'skin_tones': return masterDataConfigAPI.updateSkinTone; // New: Add skin tone update method
      case 'ethnicities': return masterDataConfigAPI.updateEthnicity; // New: Add ethnicity update method
      case 'clothing_types': return masterDataConfigAPI.updateClothingType; // New: Add clothing type update method
      case 'bust_finishes': return masterDataConfigAPI.updateBustFinish; // New: Add bust finish update method
      case 'media_qualities': return masterDataConfigAPI.updateMediaQuality; // New: Add media quality update method
      case 'generation_statuses': return masterDataConfigAPI.updateGenerationStatus;
      case 'industry_types': return masterDataConfigAPI.updateIndustryType;
      case 'category_mappings': return masterDataConfigAPI.updateCategoryMapping;
      case 'category_model_mappings': return masterDataConfigAPI.updateCategoryModelMapping; // Add category model mapping update method
      case 'credit_settings': return masterDataConfigAPI.updateCreditSetting;
      case 'bust_types': return masterDataConfigAPI.updateBustType; // New: Add bust type update method
      case 'app_versions': return appVersionAPI.updateAppVersion; // New: Add app version update method
      case 'category_gender_mappings': return masterDataConfigAPI.updateCategoryGenderMapping;
      case 'restricted_email_domains': return masterDataConfigAPI.updateRestrictedDomain;
      case 'addonServices': return modelFaceAPI.updateAddonService; // New: Add addon service update method
      case 'themes': return masterDataConfigAPI.updateTheme; // New: Add theme update method
      default: return null;
    }
  };

  const getDeleteMethod = (type) => {
    switch (type) {
      case 'model_types': return masterDataConfigAPI.deleteModelType;
      case 'output_formats': return masterDataConfigAPI.deleteOutputFormat;
      case 'categories': return masterDataConfigAPI.deleteCategory;
      case 'materials': return masterDataConfigAPI.deleteMaterial;
      case 'background_types': return masterDataConfigAPI.deleteBackgroundType;
      case 'bust_colors': return masterDataConfigAPI.deleteBustColor;
      case 'gender_options': return masterDataConfigAPI.deleteGenderOption;
      case 'skin_tones': return masterDataConfigAPI.deleteSkinTone; // New: Add skin tone delete method
      case 'ethnicities': return masterDataConfigAPI.deleteEthnicity; // New: Add ethnicity delete method
      case 'clothing_types': return masterDataConfigAPI.deleteClothingType; // New: Add clothing type delete method
      case 'bust_finishes': return masterDataConfigAPI.deleteBustFinish; // New: Add bust finish delete method
      case 'media_qualities': return masterDataConfigAPI.deleteMediaQuality; // New: Add media quality delete method
      case 'generation_statuses': return masterDataConfigAPI.deleteGenerationStatus;
      case 'industry_types': return masterDataConfigAPI.deleteIndustryType;
      case 'category_mappings': return masterDataConfigAPI.deleteCategoryMapping;
      case 'category_model_mappings': return masterDataConfigAPI.deleteCategoryModelMapping; // Add category model mapping delete method
      case 'credit_settings': return masterDataConfigAPI.deleteCreditSetting;
      case 'bust_types': return masterDataConfigAPI.deleteBustType; // New: Add bust type delete method
      case 'app_versions': return appVersionAPI.deleteAppVersion; // New: Add app version delete method
      case 'category_gender_mappings': return masterDataConfigAPI.deleteCategoryGenderMapping;
      case 'restricted_email_domains': return masterDataConfigAPI.deleteRestrictedDomain;
      case 'addonServices': return modelFaceAPI.deleteAddonService; // New: Add addon service delete method
      case 'themes': return masterDataConfigAPI.deleteTheme; // New: Add theme delete method
      default: return null;
    }
  };

  // Function to get proper button text for each tab
  const getButtonText = (title) => {
    // Special cases for titles that don't follow the simple "remove last character" rule
    const specialCases = {
      'Generation Status': 'Generation Status',
      'Clothing Types': 'Clothing Type',
      'Industry Types': 'Industry Type',
      'Bust Colors': 'Bust Color',
      'Bust Finishes': 'Bust Finish',
      'Media Qualities': 'Media Quality',
      'Skin Tones': 'Skin Tone',
      'Model Types': 'Model Type',
      'Output Formats': 'Output Format',
      'Background Types': 'Background Type',
      'Gender Options': 'Gender Option',
      'Bust Types': 'Bust Type',
      'Category Mappings': 'Category Mapping',
      'Category Model Mappings': 'Category Model Mapping', // Add button text for category model mappings
      'App Versions': 'App Version', // Add button text for app versions
      'Category Gender Mappings': 'Category Gender Mapping',
      'Restricted Domains': 'Restricted Domain',
      'Categories': 'Category',
      'Restricted Domains': 'Restricted Domain',
      'Addon Services': 'Addon Service', // New: Add button text for addon services
      'Themes': 'Theme', // New: Add button text for themes
    };

    // If it's a special case, return the mapped value
    if (specialCases[title]) {
      return specialCases[title];
    }

    // For other cases, remove the last character (plural to singular)
    return title.slice(0, -1);
  };

  const getTabData = (tabIndex) => {
    switch (tabIndex) {
      case 0: return {
        data: industryTypes, type: 'industry_types', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Industry Types'
      };
      case 1: return {
        data: categories, type: 'categories', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'icon_url',
            label: 'Preview Image',
            sortable: true,
            format: (value, row) => {
              if (!value) return '-';
              return (
                <SecureImage
                  src={getPreviewImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load category preview:', e.target.src);
                  }}
                />
              );
            }
          },
          {
            id: 'industry_type',
            label: 'Industry',
            sortable: true,
            format: (value, row) => {
              const industry = industryTypes.find(it => it.id === row.industry_type_id);
              return industry ? (industry.display_name || industry.name) : '-';
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Categories'
      };
      case 2: return {
        data: materials, type: 'materials', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'preview_image_url', label: 'Preview Image URL', sortable: true },
          {
            id: 'industry_type',
            label: 'Industry',
            sortable: true,
            format: (value, row) => {
              const industry = industryTypes.find(it => it.id === row.industry_type_id);
              return industry ? (industry.display_name || industry.name) : '-';
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Materials'
      };
      case 3: return {
        data: modelTypes, type: 'model_types', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'icon',
            label: 'Icon',
            sortable: true,
            format: (value, row) => {
              if (!row.icon) return '-';
              try {
                const IconComponent = icons[row.icon];
                if (!IconComponent) return row.icon;
                return React.createElement(IconComponent, {
                  sx: { fontSize: '20px', color: '#666' }
                });
              } catch (e) {
                console.warn('Icon not found:', row.icon);
                return row.icon || '-';
              }
            }
          },
          {
            id: 'preview_image_url',
            label: 'Preview Image',
            sortable: false,
            format: (value, row) => {
              if (!value) return '-';
              return (
                <SecureImage
                  src={getPreviewImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load model type preview:', e.target.src);
                  }}
                />
              );
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Model Types'
      };
      case 4: return {
        data: outputFormats, type: 'output_formats', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'icon',
            label: 'Icon',
            sortable: true,
            format: (value, row) => {
              if (!value) return '-';
              try {
                const IconComponent = icons[value];
                if (!IconComponent) return value;
                return React.createElement(IconComponent, {
                  sx: { fontSize: '20px', color: '#666' }
                });
              } catch (error) {
                console.warn(`Invalid icon: ${value}`);
                return value;
              }
            }
          },
          {
            id: 'preview_image_url',
            label: 'Preview Image',
            sortable: false,
            format: (value, row) => {
              if (!value) return '-';
              return (
                <SecureImage
                  src={getPreviewImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load output format preview:', e.target.src);
                  }}
                />
              );
            }
          },
          { id: 'base_credits', label: 'Base Credits', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Output Formats'
      };
      case 20: return {
        data: restrictedDomains, type: 'restricted_email_domains', columns: [
          { id: 'domain', label: 'Domain', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          {
            id: 'created_at',
            label: 'Created At',
            sortable: true,
            format: (val) => val ? new Date(val).toLocaleString() : '-'
          }
        ], title: 'Restricted Domains'
      };
      case 21: return {
        data: addonServices,
        type: 'addonServices',
        columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'code', label: 'Code', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'icon_name',
            label: 'Icon',
            sortable: true,
            format: (value) => {
              if (!value) return '-';
              try {
                const IconComponent = icons[value];
                return IconComponent ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {React.createElement(IconComponent, { style: { fontSize: 20 } })}
                    <Typography variant="caption">{value}</Typography>
                  </Box>
                ) : value;
              } catch (e) {
                return value;
              }
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ],
        title: 'Addon Services'
      };
      case 22: return {
        data: themes,
        type: 'themes',
        columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ],
        title: 'Themes'
      };
      case 5: return {
        data: backgroundTypes, type: 'background_types', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Background Types'
      };
      case 6: return {
        data: mannequinColors, type: 'bust_colors', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'hex_color',
            label: 'Hex Color',
            sortable: true,
            format: (value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '2px',
                  bgcolor: value || 'transparent',
                  border: '1px solid #eee'
                }} />
                <Typography variant="body2">{value || '-'}</Typography>
              </Box>
            )
          },
          { id: 'preview_image_url', label: 'Preview Image URL', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Bust Colors'
      };
      case 7: return {
        data: genderOptions, type: 'gender_options', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'icon',
            label: 'Icon',
            sortable: true,
            format: (value, row) => {
              if (!row.icon) return '-';
              try {
                const IconComponent = icons[row.icon];
                if (!IconComponent) return row.icon;
                return React.createElement(IconComponent, {
                  sx: { fontSize: '20px', color: '#666' }
                });
              } catch (e) {
                console.warn('Icon not found:', row.icon);
                return row.icon || '-';
              }
            }
          },
          {
            id: 'preview_image_url',
            label: 'Preview Image',
            sortable: false,
            format: (value, row) => {
              if (!value) return '-';
              return (
                <SecureImage
                  src={getPreviewImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load gender option preview:', e.target.src);
                  }}
                />
              );
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Gender Options'
      };
      case 8: return {
        data: skinTones, type: 'skin_tones', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'hex_color',
            label: 'Hex Color',
            sortable: true,
            format: (value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '2px',
                  bgcolor: value || 'transparent',
                  border: '1px solid #eee'
                }} />
                <Typography variant="body2">{value || '-'}</Typography>
              </Box>
            )
          },
          { id: 'preview_image_url', label: 'Preview Image URL', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Skin Tones'
      }; // New: Skin Tones tab
      case 9: return {
        data: ethnicities, type: 'ethnicities', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'preview_image_url',
            label: 'Preview Image',
            sortable: true,
            format: (value, row) => {
              if (!value) return '-';
              return (
                <SecureImage
                  src={getPreviewImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load ethnicity preview:', e.target.src);
                  }}
                />
              );
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Ethnicities'
      }; // New: Ethnicities tab
      case 10: return {
        data: clothingTypes, type: 'clothing_types', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'preview_image_url',
            label: 'Preview Image',
            sortable: true,
            format: (value, row) => {
              if (!value) return '-';
              return (
                <SecureImage
                  src={getPreviewImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load clothing type preview:', e.target.src);
                  }}
                />
              );
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Clothing Types'
      }; // New: Clothing Types tab
      case 11: return {
        data: bustFinishes, type: 'bust_finishes', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'preview_image_url',
            label: 'Preview Image',
            sortable: true,
            format: (value, row) => {
              if (!value) return '-';

              return (
                <SecureImage
                  src={getBustFinishImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load bust finish preview:', e.target.src);
                  }}
                  onLoad={() => {
                    // console.log('Bust finish preview loaded successfully:', getBustFinishImageUrl(value));
                  }}
                />
              );
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Bust Finishes'
      }; // New: Bust Finishes tab
      case 12: return {
        data: mediaQualities, type: 'media_qualities', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'applies_to', label: 'Applies To', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          {
            id: 'icon',
            label: 'Icon',
            sortable: false,
            format: (value, row) => {
              if (!value) return '-';
              try {
                const IconComponent = icons[value];
                if (!IconComponent) return value;
                return React.createElement(IconComponent, {
                  sx: { fontSize: '20px', color: '#666' }
                });
              } catch (error) {
                console.warn(`Invalid icon: ${value}`);
                return value;
              }
            }
          },
          {
            id: 'preview_image_url',
            label: 'Preview Image',
            sortable: false,
            format: (value, row) => {
              if (!value) return '-';
              return (
                <SecureImage
                  src={getPreviewImageUrl(value)}
                  alt={`${row.display_name || row.name} preview`}
                  inline={true}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    console.error('Failed to load media quality preview:', e.target.src);
                  }}
                />
              );
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Media Qualities'
      }; // New: Video Qualities tab
      case 13: return {
        data: generationStatuses, type: 'generation_statuses', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'color_code', label: 'Color Code', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Generation Status'
      };
      case 14: return {
        data: bustTypes, type: 'bust_types', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'preview_image_url', label: 'Preview Image URL', sortable: true },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'sort_order', label: 'Order', sortable: true }
        ], title: 'Bust Types'
      };
      case 15: return {
        data: categoryMappings, type: 'category_mappings', columns: [
          {
            id: 'category_name',
            label: 'Category',
            sortable: true,
            format: (value, row) => row.category_name || row.category?.display_name || '-'
          },
          {
            id: 'model_type_name',
            label: 'Model Type',
            sortable: true,
            format: (value, row) => row.model_type_name || '-'
          },
          {
            id: 'ethnicity_name',
            label: 'Ethnicity',
            sortable: true,
            format: (value, row) => row.ethnicity_name || '-'
          },
          {
            id: 'clothing_type_name',
            label: 'Clothing Type',
            sortable: true,
            format: (value, row) => row.clothing_type_name || '-'
          },
          {
            id: 'bust_finish_name',
            label: 'Bust Finish',
            sortable: true,
            format: (value, row) => row.bust_finish_name || '-'
          },
          {
            id: 'bust_color_name',
            label: 'Bust Color',
            sortable: true,
            format: (value, row) => row.bust_color_name || '-'
          },
          { id: 'sort_order', label: 'Sort Order', sortable: true },
          { id: 'is_active', label: 'Status', type: 'boolean', sortable: true }
        ], title: 'Category Mappings'
      };
      case 16: return {
        data: categoryModelMappings, type: 'category_model_mappings', columns: [
          {
            id: 'category_name',
            label: 'Category',
            sortable: true,
            format: (value, row) => row.category_name || '-'
          },
          {
            id: 'model_type_name',
            label: 'Model Type',
            sortable: true,
            format: (value, row) => row.model_type_name || '-'
          },
          { id: 'sort_order', label: 'Sort Order', sortable: true },
          { id: 'is_active', label: 'Status', type: 'boolean', sortable: true }
        ], title: 'Category Model Mappings'
      };
      case 17: return {
        data: appVersions, type: 'app_versions', columns: [
          { id: 'android_version_name', label: 'Android Version', sortable: true },
          { id: 'ios_version_name', label: 'iOS Version', sortable: true },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'android_version_code', label: 'Android Code', sortable: true },
          { id: 'ios_version_code', label: 'iOS Code', sortable: true },
          { id: 'android_update_redirection_url', label: 'Android Store URL', sortable: true, format: (value) => value ? (value.length > 30 ? value.substring(0, 30) + '...' : value) : '-' },
          { id: 'ios_update_redirection_url', label: 'iOS Store URL', sortable: true, format: (value) => value ? (value.length > 30 ? value.substring(0, 30) + '...' : value) : '-' },
          { id: 'android_deep_link_schema', label: 'Android Schema', sortable: true },
          { id: 'ios_deep_link_schema', label: 'iOS Schema', sortable: true },
          { id: 'min_required_version', label: 'Min Version', sortable: true, format: (value) => value || '-' },
          { id: 'universal_link_domain', label: 'Universal Links', sortable: true, format: (value) => value || '-' },
          { id: 'android_app_link_domain', label: 'App Links', sortable: true, format: (value) => value || '-' },
          { id: 'release_date', label: 'Release Date', sortable: true, format: (value) => value ? formatDate(value) : '-' },
          { id: 'enable_deep_linking', label: 'Deep Linking', sortable: true, format: (value) => value ? 'Enabled' : 'Disabled' },
          { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
          { id: 'created_at', label: 'Created', format: (value) => formatDate(value), sortable: true }
        ], title: 'App Versions'
      };
      case 18: return { data: [], type: 'system_settings', columns: [], title: 'System Settings' }; // System Settings don't use the generic table
      case 19: return {
        data: categoryGenderMappings, type: 'category_gender_mappings', columns: [
          {
            id: 'category_name',
            label: 'Category',
            sortable: true,
            format: (value, row) => row.category_name || '-'
          },
          {
            id: 'gender_option_name',
            label: 'Gender Option',
            sortable: true,
            format: (value, row) => row.gender_option_name || '-'
          },
          { id: 'sort_order', label: 'Sort Order', sortable: true },
          { id: 'is_active', label: 'Status', type: 'boolean', sortable: true }
        ], title: 'Category Gender Mappings'
      };
      case 20: return {
        data: restrictedDomains, type: 'restricted_email_domains', columns: [
          { id: 'domain', label: 'Domain', sortable: true },
          { id: 'created_at', label: 'Created At', sortable: true, format: (value) => formatDate(value) }
        ], title: 'Restricted Domains'
      };
      case 21: return {
        data: addonServices, type: 'addonServices', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { id: 'code', label: 'Service Code', sortable: true },
          { id: 'icon_name', label: 'Icon', sortable: true },
          { id: 'is_active', label: 'Status', type: 'boolean', sortable: true }
        ], title: 'Addon Services'
      };
      case 22: return {
        data: themes, type: 'themes', columns: [
          { id: 'name', label: 'Name', sortable: true },
          { id: 'display_name', label: 'Display Name', sortable: true },
          { 
            id: 'icon', 
            label: 'Icon', 
            sortable: true,
            format: (value) => {
              if (!value) return '-';
              try {
                const IconComponent = icons[value];
                return IconComponent ? React.createElement(IconComponent, { sx: { fontSize: 20 } }) : value;
              } catch (e) {
                return value;
              }
            }
          },
          { id: 'description', label: 'Description', sortable: true },
          { id: 'preview_image_url', label: 'Preview Image', sortable: true, format: (value) => value ? <img src={getPreviewImageUrl(value)} alt="Theme Preview" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /> : '-' },
          { id: 'sort_order', label: 'Sort Order', sortable: true },
          { id: 'is_active', label: 'Status', type: 'boolean', sortable: true }
        ], title: 'Themes'
      };

      default: return { data: [], type: '', columns: [], title: '' };
    }
  };

  // Format date in DD/MM/YYYY format for better readability
  // Uses toLocaleDateString with timezone options to ensure consistent Indian time display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // Use toLocaleDateString with timezone options for consistent Indian time display
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  // Pagination handlers
  const handlePageChange = (type, newPage) => {
    const tabKey = getTabKey(type);
    setPaginationState(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], page: newPage }
    }));
  };

  const handleRowsPerPageChange = (type, newRowsPerPage) => {
    const tabKey = getTabKey(type);
    setPaginationState(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], page: 0, rowsPerPage: newRowsPerPage }
    }));
  };

  // Sorting handlers
  const handleSortChange = (type, property) => {
    const tabKey = getTabKey(type);

    setSortingState(prev => {
      const currentSort = prev[tabKey];
      const isAsc = currentSort.orderBy === property && currentSort.order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';

      return {
        ...prev,
        [tabKey]: {
          orderBy: property,
          order: newOrder
        }
      };
    });

    // Reset to first page when sorting changes
    setPaginationState(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], page: 0 }
    }));
  };

  // Render industry type filter for jewelry categories and materials
  const renderIndustryTypeFilter = (type) => {
    if (type !== 'categories' && type !== 'materials') {
      return null;
    }

    const filterValue = type === 'categories'
      ? categoryIndustryFilter
      : materialIndustryFilter;

    const tabKey = getTabKey(type);

    const setFilterValue = (value) => {
      if (type === 'categories') {
        setCategoryIndustryFilter(value);
        // Reset to first page when changing filter
        setPaginationState(prev => ({
          ...prev,
          categories: {
            ...prev.categories,
            page: 0
          }
        }));
      } else {
        setMaterialIndustryFilter(value);
        // Reset to first page when changing filter
        setPaginationState(prev => ({
          ...prev,
          materials: {
            ...prev.materials,
            page: 0
          }
        }));
      }
      // Don't call loadCategories or loadMaterials here
      // The useEffect will handle the data loading when the state updates
    };

    return (
      <Box mb={2} display="flex" justifyContent="flex-start" sx={{ marginTop: 2, marginBottom: 2 }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 250, backgroundColor: 'white' }}>
          <InputLabel>Filter by Industry</InputLabel>
          <Select
            value={filterValue || ''}
            onChange={(e) => setFilterValue(e.target.value || '')}
            label="Filter by Industry"
          >
            <MenuItem value="all">
              <em>All Industries</em>
            </MenuItem>
            {industryTypes
              .filter(it => it.is_active)
              .map((industry) => (
                <MenuItem key={industry.id} value={industry.id}>
                  {industry.display_name || industry.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const getTabIndexByType = (type) => {
    const typeToTabIndex = {
      'industry_types': 0,
      'categories': 1,
      'materials': 2,
      'model_types': 3,
      'output_formats': 4,
      'background_types': 5,
      'bust_colors': 6,
      'gender_options': 7,
      'skin_tones': 8,
      'ethnicities': 9,
      'clothing_types': 10,
      'bust_finishes': 11,
      'media_qualities': 12,
      'generation_statuses': 13,
      'bust_types': 14,
      'category_mappings': 15,
      'category_model_mappings': 16,
      'app_versions': 17,
      'system_settings': 18,
      'category_gender_mappings': 19,
      'restricted_email_domains': 20,
      'addonServices': 21,
      'themes': 22
    };
    return typeToTabIndex[type] || 0;
  };

  const renderDataTable = (data, type) => {
    const tabKey = getTabKey(type);
    const pagination = paginationState[tabKey];
    const totalCount = totalCounts[tabKey];
    const sorting = sortingState[tabKey];

    // Get column definitions for this data type
    const tabIndex = getTabIndexByType(type);
    const tabData = getTabData(tabIndex);
    const columns = tabData.columns || [];

    // Fallback to default columns if no custom columns defined
    const defaultColumns = [
      { id: 'name', label: 'Name', sortable: true },
      { id: 'display_name', label: 'Display Name', sortable: true },
      { id: 'description', label: 'Description', sortable: true },
      { id: 'is_active', label: 'Active', type: 'boolean', sortable: true },
      { id: 'sort_order', label: 'Order', sortable: true }
    ];

    const finalColumns = columns.length > 0 ? columns : defaultColumns;

    // console.log('renderDataTable for type:', type, 'tabIndex:', tabIndex, 'tabData:', tabData, 'using columns:', finalColumns);

    if (!pagination) {
      console.error('Pagination data is undefined for tabKey:', tabKey);
      return (
        <>
          {renderIndustryTypeFilter(type)}
          <Box p={3} textAlign="center">
            <CircularProgress />
            <Typography>Loading pagination data...</Typography>
          </Box>
        </>
      );
    }

    // Handle case when data is not yet loaded
    if (!data) {
      return (
        <>
          {renderIndustryTypeFilter(type)}
          <Box p={3} textAlign="center">
            <CircularProgress />
            <Typography>Loading {type.replace('_', ' ')}...</Typography>
          </Box>
        </>
      );
    }

    // Get appropriate icon and message for empty state based on data type
    const getEmptyStateConfig = (dataType) => {
      switch (dataType) {
        case 'industry_types':
          return {
            icon: <Category sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No industry types found',
            message: canCreate ?
              "Click the 'New Industry Type' button above to create your first industry type." :
              "No industry types have been created yet."
          };
        case 'categories':
          return {
            icon: <Category sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No categories found',
            message: canCreate ?
              "Click the 'New Category' button above to create your first category." :
              "No categories have been created yet."
          };
        case 'materials':
          return {
            icon: <Style sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No materials found',
            message: canCreate ?
              "Click the 'New Material' button above to create your first material." :
              "No materials have been created yet."
          };
        case 'model_types':
          return {
            icon: <PhotoCamera sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No model types found',
            message: canCreate ?
              "Click the 'New Model Type' button above to create your first model type." :
              "No model types have been created yet."
          };
        case 'output_formats':
          return {
            icon: <VideoLibrary sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No output formats found',
            message: canCreate ?
              "Click the 'New Output Format' button above to create your first output format." :
              "No output formats have been created yet."
          };
        case 'background_types':
          return {
            icon: <Palette sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No background types found',
            message: canCreate ?
              "Click the 'New Background Type' button above to create your first background type." :
              "No background types have been created yet."
          };
        case 'bust_colors':
          return {
            icon: <Palette sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No bust colors found',
            message: canCreate ?
              "Click the 'New Bust Color' button above to create your first bust color." :
              "No bust colors have been created yet."
          };
        case 'gender_options':
          return {
            icon: <Style sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No gender options found',
            message: canCreate ?
              "Click the 'New Gender Option' button above to create your first gender option." :
              "No gender options have been created yet."
          };
        case 'skin_tones':
          return {
            icon: <Palette sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No skin tones found',
            message: canCreate ?
              "Click the 'New Skin Tone' button above to create your first skin tone." :
              "No skin tones have been created yet."
          };
        case 'ethnicities':
          return {
            icon: <Style sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No ethnicities found',
            message: canCreate ?
              "Click the 'New Ethnicity' button above to create your first ethnicity." :
              "No ethnicities have been created yet."
          };
        case 'clothing_types':
          return {
            icon: <Style sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No clothing types found',
            message: canCreate ?
              "Click the 'New Clothing Type' button above to create your first clothing type." :
              "No clothing types have been created yet."
          };
        case 'bust_finishes':
          return {
            icon: <Style sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No bust finishes found',
            message: canCreate ?
              "Click the 'New Bust Finish' button above to create your first bust finish." :
              "No bust finishes have been created yet."
          };
        case 'media_qualities':
          return {
            icon: <VideoLibrary sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No media qualities found',
            message: canCreate ?
              "Click the 'New Media Quality' button above to create your first media quality." :
              "No media qualities have been created yet."
          };
        case 'generation_statuses':
          return {
            icon: <Settings sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No generation status found',
            message: canCreate ?
              "Click the 'New Generation Status' button above to create your first generation status." :
              "No generation status have been created yet."
          };
        case 'bust_types':
          return {
            icon: <Style sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No bust types found',
            message: canCreate ?
              "Click the 'New Bust Type' button above to create your first bust type." :
              "No bust types have been created yet."
          };
        case 'app_versions':
          return {
            icon: <Settings sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No app versions found',
            message: canCreate ?
              "Click the 'New App Version' button above to create your first app version." :
              "No app versions have been created yet."
          };
        case 'category_gender_mappings':
          return {
            icon: <Settings sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No category gender mappings found',
            message: canCreate ?
              "Click the 'New Category Gender Mapping' button above to create your first category gender mapping." :
              "No category gender mappings have been created yet."
          };
        case 'restricted_email_domains':
          return {
            icon: <Block sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No restricted domains found',
            message: canCreate ?
              "Click the 'New Restricted Domain' button above to add a domain to the blocklist." :
              "No restricted domains have been added yet."
          };
        case 'addonServices':
          return {
            icon: <icons.Star sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: 'No addon services found',
            message: canCreate ?
              "Click the 'New Addon Service' button above to create your first addon service." :
              "No addon services have been created yet."
          };
        default:
          return {
            icon: <Settings sx={{ fontSize: 60, color: 'text.disabled' }} />,
            title: `No ${type.replace('_', ' ')} found`,
            message: canCreate ?
              `Click the 'New ${getButtonText(type.replace('_', ' '))}' button above to create your first ${type.replace('_', ' ')}.` :
              `No ${type.replace('_', ' ')} have been created yet.`
          };
      }
    };

    // Handle empty data with proper empty state UI
    if (!Array.isArray(data) || data.length === 0) {
      const emptyStateConfig = getEmptyStateConfig(type);
      return (
        <>
          {renderIndustryTypeFilter(type)}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {finalColumns.map((column) => (
                    <TableCell key={column.id}>
                      {column.sortable !== false ? (
                        <TableSortLabel
                          active={sorting.orderBy === column.id}
                          direction={sorting.orderBy === column.id ? sorting.order : 'asc'}
                          onClick={() => handleSortChange(type, column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={finalColumns.length + 1} align="center">
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      {emptyStateConfig.icon}
                      <Typography variant="h6" color="text.secondary">
                        {emptyStateConfig.title}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 500 }}>
                        {emptyStateConfig.message}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={pagination.page}
            onPageChange={(e, newPage) => handlePageChange(type, newPage)}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={(e) => handleRowsPerPageChange(type, parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      );
    }

    return (
      <>
        {renderIndustryTypeFilter(type)}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {finalColumns.map((column) => (
                  <TableCell key={column.id}>
                    {column.sortable !== false ? (
                      <TableSortLabel
                        active={sorting.orderBy === column.id}
                        direction={sorting.orderBy === column.id ? sorting.order : 'asc'}
                        onClick={() => handleSortChange(type, column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => (
                <TableRow key={item.id}>
                  {finalColumns.map((column) => (
                    <TableCell key={column.id}>
                      {column.type === 'boolean' ? (
                        <Chip
                          label={item[column.id] ? 'Active' : 'Inactive'}
                          color={item[column.id] ? 'success' : 'error'}
                          size="small"
                        />
                      ) : column.format ? (
                        <Typography variant="body2">
                          {column.format(item[column.id], item)}
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          {item[column.id] || '-'}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {canUpdate && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(item, type)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item, type)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={(e, newPage) => handlePageChange(type, newPage)}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={(e) => handleRowsPerPageChange(type, parseInt(e.target.value, 10))}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </>
    );
  };

  const renderSystemSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">System Settings</Typography>
          {canUpdate && (
            <Button
              variant="outlined"
              startIcon={<Restore />}
              onClick={handleResetToDefaults}
            >
              Reset to Defaults
            </Button>
          )}
        </Box>
      </Grid>

      {/* Credit Settings Section */}
      <Grid item xs={12}>
        <Card elevation={2}>
          <CardHeader
            title="Credit Configuration"
            subheader="Configure credit costs for different media types"
          />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Watermark Cost (Credits)"
                  type="number"
                  value={formData.watermark_cost !== undefined && formData.watermark_cost !== null ? Math.abs(formData.watermark_cost) : ''}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    // Only allow positive values or zero
                    if (value >= 0) {
                      setFormData({ ...formData, watermark_cost: value });
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"></InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText="Cost to remove watermark. Only positive values allowed."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Brand Logo Cost (Credits)"
                  type="number"
                  value={formData.brand_logo_cost !== undefined && formData.brand_logo_cost !== null ? formData.brand_logo_cost : ''}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    // Only allow positive values or zero
                    if (value >= 0) {
                      setFormData({ ...formData, brand_logo_cost: value });
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveCreditSettings}
                  startIcon={<Save />}
                >
                  Save Credit Settings
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* System Settings Section */}
      <Grid item xs={12}>
        <Card elevation={2}>
          <CardHeader
            title="System Configuration"
            subheader="Configure system-wide settings and limits. Note: Maximum credit allocation limit is configured in database only."
            action={
              canUpdate && (
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadSystemSettings}
                  size="small"
                >
                  Refresh
                </Button>
              )
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              {/* New User Credits Setting */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Default Credits for New Merchants"
                  type="number"
                  value={systemSettings.NEW_USER_CREDITS?.typed_value ?? systemSettings.NEW_USER_CREDITS?.setting_value ?? 1000}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1000;
                    if (value >= 0) {
                      setSystemSettings(prev => ({
                        ...prev,
                        NEW_USER_CREDITS: {
                          ...prev.NEW_USER_CREDITS,
                          typed_value: value
                        }
                      }));
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">🎁</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={`Credits automatically given to new merchants upon registration (Current: ${systemSettings.NEW_USER_CREDITS?.typed_value ?? systemSettings.NEW_USER_CREDITS?.setting_value ?? 1000})`}
                  disabled={!canUpdate}
                />
              </Grid>

              {/* Low Credit Threshold Setting */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Low Credit Alert Threshold"
                  type="number"
                  value={systemSettings.LOW_CREDIT_THRESHOLD?.typed_value ?? systemSettings.LOW_CREDIT_THRESHOLD?.setting_value ?? 500}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 500;
                    if (value >= 0) {
                      setSystemSettings(prev => ({
                        ...prev,
                        LOW_CREDIT_THRESHOLD: {
                          ...prev.LOW_CREDIT_THRESHOLD,
                          typed_value: value
                        }
                      }));
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">⚠️</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={`Credit balance below which low credit alerts are sent to merchants (Current: ${systemSettings.LOW_CREDIT_THRESHOLD?.typed_value ?? systemSettings.LOW_CREDIT_THRESHOLD?.setting_value ?? 500})`}
                  disabled={!canUpdate}
                />
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveSystemSettings}
                  startIcon={<Save />}
                  disabled={!canUpdate}
                >
                  Save System Configuration
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  if (!canView) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to view Master Data Configuration.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            Master Data Configuration
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadAllData}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            console.log(`🔄 Tab changed from ${activeTab} to ${newValue}`);
            if (newValue === 3) {
              console.log('📋 Switching to Model Types tab (index 3)');
            }
            setActiveTab(newValue);
          }}
          variant="scrollable" // Enable horizontal scrolling
          scrollButtons="auto" // Show scroll buttons when needed
          allowScrollButtonsMobile // Allow scroll buttons on mobile
        >
          <Tab label="Industry Types" />
          <Tab label="Categories" />
          <Tab label="Materials" />
          <Tab label="Model Types" />
          <Tab label="Output Formats" />
          <Tab label="Background Types" />
          <Tab label="Bust Colors" />
          <Tab label="Gender Options" />
          <Tab label="Skin Tones" /> {/* New: Skin Tones tab */}
          <Tab label="Ethnicities" /> {/* New: Ethnicities tab */}
          <Tab label="Clothing Types" /> {/* New: Clothing Types tab */}
          <Tab label="Bust Finishes" /> {/* New: Bust Finishes tab */}
          <Tab label="Media Qualities" /> {/* New: Media Qualities tab */}
          <Tab label="Generation Status" />
          <Tab label="Bust Types" /> {/* New: Bust Types tab */}
          <Tab label="Category Mappings" /> {/* New: Category Mappings tab */}
          <Tab label="Category Model Mappings" /> {/* New: Category Model Mappings tab */}
          <Tab label="App Versions" /> {/* New: App Versions tab */}
          <Tab label="System Settings" />
          <Tab label="Category Gender Mappings" />
          <Tab label="Restricted Domains" />
          <Tab label="Addon Services" /> {/* New: Addon Services tab */}
          <Tab label="Themes" /> {/* New: Themes tab */}
          <Tab label="Currencies" />
          <Tab label="Tax Rules" />
        </Tabs>
      </Paper>

      {/* Content */}
      <Paper elevation={2} sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <LinearProgress />
          </Box>
        ) : activeTab === 18 ? ( /* System Settings tab is now at index 18 */
          renderSystemSettings()
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6">
                {getTabData(activeTab).title}
              </Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => {
                    if (activeTab === 15) {
                      // Special handling for Category Mappings tab
                      loadCategoryMappings();
                      loadAllCategories();
                      loadAllModelTypes();
                      loadMaterials();
                      loadEthnicities();
                      loadClothingTypes();
                      loadAllBustFinishes();
                      loadAllBustColors();
                    } else if (activeTab === 16) {
                      // Special handling for Category Model Mappings tab
                      loadCategoryModelMappings();
                      loadAllCategories();
                      loadAllModelTypes();
                    } else if (activeTab === 17) {
                      // Special handling for App Versions tab
                      loadAppVersions();
                    } else if (activeTab === 19) {
                      loadCategoryGenderMappings();
                      loadAllCategories();
                      loadGenderOptions();
                    } else {
                      loadAllData();
                    }
                  }}
                  size="small"
                >
                  Refresh
                </Button>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleNew(getTabData(activeTab).type)}
                  >
                    {`New ${getButtonText(getTabData(activeTab).title)}`}
                  </Button>
                )}
              </Box>
            </Box>
            {activeTab === 21 && renderDataTable(addonServices, 'addonServices')}
            {activeTab === 22 && renderDataTable(themes, 'themes')}
            {activeTab === 23 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={700}>Subscription Currencies</Typography>
                  {canCreate && (
                    <Button variant="contained" startIcon={<Add />} onClick={() => openV2Dialog('currency')}>New Currency</Button>
                  )}
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Display Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Symbol</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Decimals</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Default</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Status</TableCell>
                        {(canUpdate || canDelete) && (
                          <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {v2Currencies.length === 0 && (
                        <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.disabled' }}>No currencies — click New Currency to add one</TableCell></TableRow>
                      )}
                      {v2Currencies.map(c => (
                        <TableRow key={c.id} sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: 'grey.50' } }}>
                          <TableCell><Chip label={c.code} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} /></TableCell>
                          <TableCell>{c.display_name}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{c.symbol}</TableCell>
                          <TableCell>{c.decimal_places}</TableCell>
                          <TableCell>{c.is_default ? <Chip label="Default" color="primary" size="small" /> : <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                          <TableCell>
                            <Chip
                              label={c.status === 0 ? 'Inactive' : 'Active'}
                              size="small"
                              color={c.status === 0 ? 'default' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          {(canUpdate || canDelete) && (
                            <TableCell>
                              <Box display="flex" gap={0.5}>
                                {canUpdate && (
                                  <Tooltip title="Edit">
                                    <IconButton size="small"
                                      onClick={() => openV2Dialog('currency', c)}
                                      sx={{ width: 30, height: 30, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, color: 'primary.main', '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.200' } }}>
                                      <Edit sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDelete && (
                                  <Tooltip title="Deactivate">
                                    <IconButton size="small"
                                      onClick={() => deleteV2Currency(c.id, c.code)}
                                      sx={{ width: 30, height: 30, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, color: 'error.main', '&:hover': { bgcolor: 'error.50', borderColor: 'error.200' } }}>
                                      <Delete sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            {activeTab === 24 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={700}>Subscription Tax Rules</Typography>
                  {canCreate && (
                    <Button variant="contained" startIcon={<Add />} onClick={() => openV2Dialog('tax_rule')}>New Tax Rule</Button>
                  )}
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Currency</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Supply Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Rate %</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>HSN / SAC</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Default</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Components</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Status</TableCell>
                        {(canUpdate || canDelete) && (
                          <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {v2TaxRules.length === 0 && (
                        <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.disabled' }}>No tax rules — click New Tax Rule to add one</TableCell></TableRow>
                      )}
                      {v2TaxRules.map(r => (
                        <TableRow key={r.id} sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: 'grey.50' } }}>
                          <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                          <TableCell><Chip label={r.currency_code} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} /></TableCell>
                          <TableCell>
                            <Chip
                              label={r.supply_type === 'intra_state' ? 'Intra-State' : r.supply_type === 'inter_state' ? 'Inter-State' : 'Auto'}
                              size="small" variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{r.effective_rate}%</TableCell>
                          <TableCell>{r.default_hsn_sac || <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                          <TableCell>{r.is_default ? <Chip label="Default" color="primary" size="small" /> : <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {(r.components || []).map(c => (
                                <Chip key={c.component_type} label={`${c.component_type} ${c.rate_percent}%`} size="small" sx={{ mr: 0.5 }} />
                              ))}
                              {(!r.components || r.components.length === 0) && <Typography variant="body2" color="text.disabled">—</Typography>}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={r.status === 0 ? 'Inactive' : 'Active'}
                              size="small"
                              color={r.status === 0 ? 'default' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          {(canUpdate || canDelete) && (
                            <TableCell>
                              <Box display="flex" gap={0.5}>
                                {canUpdate && (
                                  <Tooltip title="Edit">
                                    <IconButton size="small"
                                      onClick={() => openV2Dialog('tax_rule', r)}
                                      sx={{ width: 30, height: 30, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, color: 'primary.main', '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.200' } }}>
                                      <Edit sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDelete && (
                                  <Tooltip title="Deactivate">
                                    <IconButton size="small"
                                      onClick={() => deleteV2TaxRule(r.id, r.name)}
                                      sx={{ width: 30, height: 30, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, color: 'error.main', '&:hover': { bgcolor: 'error.50', borderColor: 'error.200' } }}>
                                      <Delete sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            {activeTab !== 18 && activeTab !== 21 && activeTab !== 22 && activeTab !== 23 && activeTab !== 24 && renderDataTable(getTabData(activeTab).data, getTabData(activeTab).type)}
          </>
        )}
      </Paper>

      {/* Subscription v2 Delete Confirmation */}
      <Dialog open={v2DeleteConfirm.open} onClose={() => setV2DeleteConfirm({ open: false, type: null, id: null, label: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>Confirm Deactivate</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate <strong>{v2DeleteConfirm.label}</strong>? This will hide it from active use but preserve historical records.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setV2DeleteConfirm({ open: false, type: null, id: null, label: '' })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmV2Delete}>Deactivate</Button>
        </DialogActions>
      </Dialog>



      {/* Subscription v2 Currency / Tax Rule Dialog */}
      <Dialog open={v2Dialog.open} onClose={closeV2Dialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {v2Dialog.type === 'currency'
            ? (v2Dialog.item ? 'Edit Currency' : 'New Currency')
            : (v2Dialog.item ? 'Edit Tax Rule' : 'New Tax Rule')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {v2Dialog.type === 'currency' && (
              <>
                {!v2Dialog.item && (
                  <Grid item xs={12}>
                    <TextField
                      label="Code (e.g. INR)" fullWidth required
                      value={v2Form.code || ''}
                      inputProps={{ maxLength: 3 }}
                      error={!!v2Errors.code}
                      helperText={v2Errors.code || ' '}
                      onChange={e => {
                        const val = e.target.value.toUpperCase();
                        setV2Form({ ...v2Form, code: val });
                        if (v2Touched.code) setV2Errors(er => ({ ...er, code: validateCurrencyField('code', val) }));
                      }}
                      onBlur={() => {
                        setV2Touched(t => ({ ...t, code: true }));
                        setV2Errors(er => ({ ...er, code: validateCurrencyField('code', v2Form.code) }));
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="Display Name" fullWidth required
                    value={v2Form.display_name || ''}
                    error={!!v2Errors.display_name}
                    helperText={v2Errors.display_name || ' '}
                    onChange={e => {
                      setV2Form({ ...v2Form, display_name: e.target.value });
                      if (v2Touched.display_name) setV2Errors(er => ({ ...er, display_name: validateCurrencyField('display_name', e.target.value) }));
                    }}
                    onBlur={() => {
                      setV2Touched(t => ({ ...t, display_name: true }));
                      setV2Errors(er => ({ ...er, display_name: validateCurrencyField('display_name', v2Form.display_name) }));
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Symbol" fullWidth required
                    value={v2Form.symbol || ''}
                    error={!!v2Errors.symbol}
                    helperText={v2Errors.symbol || ' '}
                    onChange={e => {
                      setV2Form({ ...v2Form, symbol: e.target.value });
                      if (v2Touched.symbol) setV2Errors(er => ({ ...er, symbol: validateCurrencyField('symbol', e.target.value) }));
                    }}
                    onBlur={() => {
                      setV2Touched(t => ({ ...t, symbol: true }));
                      setV2Errors(er => ({ ...er, symbol: validateCurrencyField('symbol', v2Form.symbol) }));
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Decimal Places" type="number" fullWidth
                    value={v2Form.decimal_places ?? 2}
                    inputProps={{ min: 0, max: 4 }}
                    error={!!v2Errors.decimal_places}
                    helperText={v2Errors.decimal_places || ' '}
                    onChange={e => {
                      const val = parseInt(e.target.value);
                      setV2Form({ ...v2Form, decimal_places: val });
                      if (v2Touched.decimal_places) setV2Errors(er => ({ ...er, decimal_places: validateCurrencyField('decimal_places', e.target.value) }));
                    }}
                    onBlur={() => {
                      setV2Touched(t => ({ ...t, decimal_places: true }));
                      setV2Errors(er => ({ ...er, decimal_places: validateCurrencyField('decimal_places', v2Form.decimal_places) }));
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch checked={!!v2Form.is_default} onChange={e => setV2Form({ ...v2Form, is_default: e.target.checked })} />} label="Set as default currency" />
                </Grid>
              </>
            )}
            {v2Dialog.type === 'tax_rule' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Name" fullWidth required
                    value={v2Form.name || ''}
                    error={!!v2Errors.name}
                    helperText={v2Errors.name || ' '}
                    onChange={e => {
                      setV2Form({ ...v2Form, name: e.target.value });
                      if (v2Touched.name) setV2Errors(er => ({ ...er, name: validateTaxRuleField('name', e.target.value) }));
                    }}
                    onBlur={() => {
                      setV2Touched(t => ({ ...t, name: true }));
                      setV2Errors(er => ({ ...er, name: validateTaxRuleField('name', v2Form.name) }));
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select label="Currency" value={v2Form.currency_code || 'INR'} onChange={e => setV2Form({ ...v2Form, currency_code: e.target.value })}>
                      {v2Currencies.map(c => <MenuItem key={c.code} value={c.code}>{c.code} – {c.display_name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Supply Type</InputLabel>
                    <Select label="Supply Type" value={v2Form.supply_type || 'intra_state'} onChange={e => setV2Form({ ...v2Form, supply_type: e.target.value })}>
                      <MenuItem value="intra_state">Intra-State</MenuItem>
                      <MenuItem value="inter_state">Inter-State</MenuItem>
                      <MenuItem value="auto">Auto</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Effective Rate %" type="number" fullWidth required
                    value={v2Form.effective_rate || ''}
                    error={!!v2Errors.effective_rate}
                    helperText={v2Errors.effective_rate || ' '}
                    onChange={e => {
                      setV2Form({ ...v2Form, effective_rate: e.target.value });
                      if (v2Touched.effective_rate) setV2Errors(er => ({ ...er, effective_rate: validateTaxRuleField('effective_rate', e.target.value) }));
                    }}
                    onBlur={() => {
                      setV2Touched(t => ({ ...t, effective_rate: true }));
                      setV2Errors(er => ({ ...er, effective_rate: validateTaxRuleField('effective_rate', v2Form.effective_rate) }));
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Default HSN/SAC" fullWidth value={v2Form.default_hsn_sac || ''} onChange={e => setV2Form({ ...v2Form, default_hsn_sac: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch checked={!!v2Form.is_default} onChange={e => setV2Form({ ...v2Form, is_default: e.target.checked })} />} label="Set as default for this currency" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Tax Components</Typography>
                  {v2TaxComponents.map((comp, i) => (
                    <Box key={i} display="flex" gap={1} mb={1} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel>Type</InputLabel>
                        <Select label="Type" value={comp.component_type} onChange={e => { const c = [...v2TaxComponents]; c[i].component_type = e.target.value; setV2TaxComponents(c); }}>
                          {['CGST','SGST','IGST','CESS','VAT','NONE'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField size="small" label="Rate %" type="number" value={comp.rate_percent} onChange={e => { const c = [...v2TaxComponents]; c[i].rate_percent = e.target.value; setV2TaxComponents(c); }} sx={{ width: 90 }} />
                      <IconButton size="small" color="error" onClick={() => setV2TaxComponents(v2TaxComponents.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<Add />} onClick={() => { setV2TaxComponents([...v2TaxComponents, { component_type: 'CGST', rate_percent: '' }]); setV2Errors(er => ({ ...er, components: '' })); }}>Add Component</Button>
                  {v2Errors.components && <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>{v2Errors.components}</Typography>}
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeV2Dialog}>Cancel</Button>
          <Button variant="contained" disabled={v2Submitting} onClick={v2Dialog.type === 'currency' ? saveV2Currency : saveV2TaxRule}>
            {v2Dialog.item ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ?
            `Edit ${dialogType === 'categories' ? 'Category' :
              dialogType === 'materials' ? 'Material' :
                dialogType === 'bust_colors' ? 'Bust Color' :
                  dialogType.replace('_', ' ')}` :
            `New ${dialogType === 'categories' ? 'Category' :
              dialogType === 'materials' ? 'Material' :
                dialogType === 'bust_colors' ? 'Bust Color' :
                  dialogType.replace('_', ' ')}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {renderFormFields(dialogType)}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={editingItem ? handleUpdate : handleCreate}
            variant="contained"
          >
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          color: 'error.main',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Delete />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete{' '}
            <strong>{itemToDelete?.display_name || itemToDelete?.name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone. The item will be permanently removed from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCancelReset}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          Reset to Defaults
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Are you sure you want to reset all system settings to defaults? This will reset credit costs to their default values.
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Reset behavior:</Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li><strong>Base costs</strong> (Image, GIF, Video): Reset to environment variable values</li>
                <li><strong>Watermark & Brand Logo costs</strong>: Reset to system setting defaults</li>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Note: Base costs are managed by system administrators via environment variables,
                while watermark and brand logo costs can be configured through this interface.
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReset} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmReset} color="error" variant="contained">
            Reset to Defaults
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon Picker Dialog */}
      <IconPicker
        open={iconPickerOpen}
        onClose={() => setIconPickerOpen(false)}
        selectedIcon={formData.icon}
        onIconSelect={(iconName) => setFormData({ ...formData, icon: iconName })}
      />
    </Container>
  );
};

export default MasterDataConfiguration; 